import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter, DrawerClose } from "@/components/ui/drawer";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Search,
  Menu,
  Sun,
  Moon,
  Zap,
  BarChart3,
  FileText,
  Clock,
  Settings,
  ChevronRight,
  ChevronDown,
  Play,
  Copy,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Target,
  Eye,
  Hash,
  Music,
  Scissors,
  MessageSquare,
  Users,
  Sparkles,
  RefreshCw,
  Download,
  Lightbulb,
  Film,
  Palette,
  Type,
  Camera,
  Layers,
  MonitorPlay,
  Wifi,
  WifiOff,
  Activity,
  Trash2,
  Plus,
  Edit3,
  Link as LinkIcon,
  Wand2,
  ImageIcon,
  Upload,
  User,
  Briefcase,
  Loader2,
  ExternalLink,
  Bell,
  X,
  Brain,
  Settings2,
  RotateCcw,
  Building2,
  Pencil,
  Instagram,
  Youtube,
  LogOut,
  Share2,
  LayoutTemplate,
  FolderOpen,
  Save,
  Package
} from "lucide-react";

import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { motion, AnimatePresence } from "framer-motion";

// Custom hooks and components
import { useAnalysisWebSocket } from "@/hooks/useAnalysisWebSocket";
import { useFolders } from "@/hooks/useFolders";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { validateImageFile } from "@/lib/imageUpload";
import { RetentionChart } from "@/components/RetentionChart";
import { StoryPreviewDialog } from "@/components/StoryPreviewDialog";
import CarouselGenerator from "@/components/CarouselGenerator";
import { ThumbnailCard } from "@/components/ThumbnailCard";
import { Generator2Page } from "@/components/Generator2";
import { StyleDetailPage } from "@/components/StyleDetailPage";
import { LogoLibraryPage } from "@/components/LogoLibraryPage";
import BrandKitsPage from "@/components/BrandKitsPage";
import WhatsNewModal, { WHATS_NEW_VERSION } from "@/components/WhatsNewModal";
// LinkedInCarouselPage removed
import ThumbnailEditor from "@/components/ThumbnailEditor";
import MetaAdEditor from "@/components/MetaAdEditor";
import { MetaAdsGenerator } from "@/components/MetaAdsGenerator";
import { MetaAdTemplatesLibrary } from "@/components/MetaAdTemplatesLibrary";
import { ComparisonAdGenerator } from "@/components/ComparisonAdGenerator";
import { ProfilePage } from "@/components/ProfilePage";
import { AdminPanel } from "@/components/AdminPanel";
import { LoginPage, AccountSetupPage } from "@/components/AuthPages";
import PasswordResetPage from "@/components/PasswordResetPage";
import { ShareDialog } from "@/components/ShareDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== Auth Helper ==============
const getAuthToken = () => localStorage.getItem("auth_token");
const getUser = () => {
  const user = localStorage.getItem("user");
  return user ? JSON.parse(user) : null;
};
const logout = () => {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("user");
  window.location.reload();
};

// Add auth header to axios requests
axios.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Add timeout - longer for AI image editing and download endpoints
  if (!config.timeout) {
    const isAIEdit = config.url && (config.url.includes('/editor/') || config.url.includes('/generate'));
    const isDownload = config.url && config.url.includes('/download');
    config.timeout = (isAIEdit || isDownload) ? 120000 : 30000;
  }
  return config;
});

// Add response interceptor to handle deploy version check + errors
axios.interceptors.response.use(
  (response) => {
    // Check deploy version on every successful API response
    const serverVersion = response.headers?.['x-deploy-version'];
    if (serverVersion) {
      const storedVersion = localStorage.getItem('deploy_version');
      if (!storedVersion) {
        // First request — store the version
        localStorage.setItem('deploy_version', serverVersion);
      } else if (storedVersion !== serverVersion) {
        // Version changed → server was redeployed → update stored version
        // Do NOT delete auth token — JWT remains valid as long as JWT_SECRET is stable
        localStorage.setItem('deploy_version', serverVersion);
        localStorage.setItem('deploy_update_notice', 'true');
        // Soft reload to pick up new frontend bundle, keeping session intact
        window.location.reload();
        return response;
      }
    }
    return response;
  },
  (error) => {
    // Log the error but don't throw for network errors during polling
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.warn('Request timeout - server may be busy');
    }
    return Promise.reject(error);
  }
);

// ============== Theme Toggle ==============
const ThemeToggle = () => {
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark') ||
        localStorage.getItem('theme') === 'dark' ||
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsDark(!isDark)}
            data-testid="theme-toggle-switch"
            className="h-9 w-9"
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{isDark ? 'Light Mode' : 'Dark Mode'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ============== Sidebar ==============
const Sidebar = ({ currentPath, activeJobsCount = 0 }) => {
  const navItems = [
    { group: "Analyze", items: [
      { icon: Zap, label: "Neue Analyse", path: "/", testId: "nav-new-analysis" },
      { icon: Clock, label: "Verlauf", path: "/history", testId: "nav-history" }
    ]},
    { group: "YouTube Studio", items: [
      { icon: Palette, label: "Style Library", path: "/styles", testId: "nav-style-library" },
      { icon: Sparkles, label: "Thumbnail Generator", path: "/generator", testId: "nav-thumbnail-generator" }
    ]},
    { group: "Meta Ads Studio", items: [
      { icon: Instagram, label: "Meta Ads Generator", path: "/meta-generator", testId: "nav-meta-generator" },
      { icon: Layers, label: "Karussell-Ads", path: "/carousel", testId: "nav-carousel" },
      { icon: Layers, label: "Ad Templates", path: "/meta-templates", testId: "nav-meta-templates" },
      { icon: TrendingUp, label: "Vergleichs-Ads", path: "/comparison-ads", testId: "nav-comparison-ads" }
    ]},
    { group: "Bibliotheken", items: [
      { icon: Users, label: "Personen", path: "/persons", testId: "nav-persons" },
      { icon: Building2, label: "Logo-Bibliothek", path: "/logos", testId: "nav-logos" },
      { icon: Package, label: "Brand Kits", path: "/brand-kits", testId: "nav-brand-kits" }
    ]},
    { group: "Tools", items: [
      { icon: FileText, label: "Script Generator", path: "/script", testId: "nav-script-generator" }
    ]},
    { group: "System", items: [
      { icon: Activity, label: "Meine Aufträge", path: "/jobs", testId: "nav-jobs", badge: activeJobsCount > 0 ? activeJobsCount : null }
    ]}
  ];

  return (
    <div className="w-[270px] border-r bg-card/50 backdrop-blur supports-[backdrop-filter]:bg-card/50 flex flex-col h-full">
      {/* Logo */}
      <div className="h-14 px-6 flex items-center border-b">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <Zap className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="font-semibold text-lg tracking-tight">Bildwerk</span>
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-6">
          {navItems.map((group, idx) => (
            <div key={idx}>
              <p className="px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                {group.group}
              </p>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const isActive = currentPath === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={item.testId}
                      className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="h-4 w-4" />
                        {item.label}
                      </div>
                      {item.badge && (
                        <Badge variant="secondary" className="h-5 px-1.5 text-xs bg-primary/20 text-primary">
                          {item.badge}
                        </Badge>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>
    </div>
  );
};

// ============== Mobile Nav ==============
const MobileNav = ({ currentPath }) => {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="lg:hidden" data-testid="nav-open-button">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[270px] p-0">
        <Sidebar currentPath={currentPath} />
      </SheetContent>
    </Sheet>
  );
};

// ============== Topbar ==============
const Topbar = ({ currentPath, onOpenCommandMenu, user, onLogout, hasUnreadUpdates, onOpenWhatsNew }) => {
  const navigate = useNavigate();
  return (
    <header className="h-14 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/80 flex items-center justify-between px-4 lg:px-6">
      <div className="flex items-center gap-3">
        <MobileNav currentPath={currentPath} />
        <div className="hidden lg:block">
          <h1 className="text-lg font-semibold tracking-tight">
            {currentPath === '/' && 'Video Analyse'}
            {currentPath === '/history' && 'Analyse Verlauf'}
            {currentPath === '/script' && 'Script Generator'}
          </h1>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {/* Command Menu Trigger */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="hidden sm:flex items-center gap-2 text-muted-foreground hover:text-foreground"
                onClick={onOpenCommandMenu}
                data-testid="command-menu-trigger"
              >
                <Search className="h-4 w-4" />
                <span className="text-xs">Suche...</span>
                <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Schnellsuche öffnen</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Notification Bell */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative h-9 w-9"
                onClick={onOpenWhatsNew}
                data-testid="notification-bell"
              >
                <Bell className="h-4 w-4" />
                {hasUnreadUpdates && (
                  <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-background" data-testid="unread-dot" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{hasUnreadUpdates ? 'Neue Updates verfügbar' : 'Updates & Changelog'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <ThemeToggle />
        
        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2">
              <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div className="hidden sm:flex flex-col items-start">
                <span className="text-sm">{user?.name || 'Benutzer'}</span>
                {user?.organization_name && (
                  <span className="text-[10px] text-muted-foreground leading-tight">{user.organization_name}</span>
                )}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                {user?.organization_name && (
                  <div className="flex items-center gap-1 pt-1">
                    <Building2 className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{user.organization_name}</span>
                  </div>
                )}
                {user?.is_super_admin && (
                  <Badge variant="secondary" className="w-fit mt-1 text-[10px]">Super-Admin</Badge>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <User className="mr-2 h-4 w-4" />
              Mein Profil
            </DropdownMenuItem>
            {(user?.role === "admin" || user?.role === "owner" || user?.is_super_admin) && (
              <>
                <DropdownMenuItem onClick={() => navigate('/admin')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Benutzerverwaltung
                </DropdownMenuItem>
              </>
            )}
            {user?.is_super_admin && (
              <DropdownMenuItem onClick={() => navigate('/admin?tab=organizations')}>
                <Building2 className="mr-2 h-4 w-4" />
                Organisationen
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="text-red-600">
              <LogOut className="mr-2 h-4 w-4" />
              Abmelden
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

// ============== Analysis Cards ==============
const InsightCard = ({ icon: Icon, title, content, badge, testId }) => (
  <Card className="card-hover" data-testid={testId}>
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="h-4 w-4 text-primary" />
          </div>
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
        </div>
        {badge && (
          <Badge variant="secondary" className="text-xs">
            {badge}
          </Badge>
        )}
      </div>
    </CardHeader>
    <CardContent>
      <p className="text-sm text-muted-foreground leading-relaxed">{content}</p>
    </CardContent>
  </Card>
);

const ViralFactorCard = ({ factor, index }) => {
  const impactColors = {
    high: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
    medium: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20',
    low: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20'
  };

  return (
    <div
      className="p-4 rounded-lg border bg-card/50 card-hover"
      data-testid={`viral-factor-${index}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-medium">
              {index + 1}
            </span>
            <h4 className="font-medium text-sm">{factor.factor}</h4>
          </div>
          <p className="text-sm text-muted-foreground">{factor.description}</p>
        </div>
        <Badge className={`text-xs ${impactColors[factor.impact] || impactColors.medium}`}>
          {factor.impact === 'high' ? 'Hoch' : factor.impact === 'medium' ? 'Mittel' : 'Niedrig'}
        </Badge>
      </div>
    </div>
  );
};

// ============== Skeleton Loaders ==============
const AnalysisSkeleton = () => (
  <div className="space-y-6 fade-in">
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {[...Array(6)].map((_, i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8 rounded-lg" />
              <Skeleton className="h-4 w-24" />
            </div>
          </CardHeader>
          <CardContent>
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-3/4" />
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

// ============== Platform Logo Components ==============
const YouTubeLogo = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const InstagramLogo = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24">
    <defs>
      <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#FFDC80" />
        <stop offset="25%" stopColor="#FCAF45" />
        <stop offset="50%" stopColor="#F77737" />
        <stop offset="75%" stopColor="#F56040" />
        <stop offset="87.5%" stopColor="#FD1D1D" />
        <stop offset="100%" stopColor="#E1306C" />
      </linearGradient>
      <linearGradient id="instagram-gradient-2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#833AB4" />
        <stop offset="50%" stopColor="#C13584" />
        <stop offset="100%" stopColor="#E1306C" />
      </linearGradient>
    </defs>
    <path fill="url(#instagram-gradient)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const PlatformIcon = ({ platform, className = "h-4 w-4" }) => {
  if (platform === 'youtube') {
    return <YouTubeLogo className={className} style={{ color: '#FF0000' }} />;
  }
  return <InstagramLogo className={className} />;
};

// ============== Home/Analyzer Page ==============
const HomePage = () => {
  const [url, setUrl] = useState('');
  const [topic, setTopic] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const navigate = useNavigate();
  
  // For this simplified version, we don't show analysis results on the home page
  const isAnalyzing = false;
  const analysis = null;
  const error = null;

  const handleAnalyze = async () => {
    if (!url) {
      toast.error('Bitte gib eine YouTube oder Instagram URL ein');
      return;
    }

    setIsStarting(true);
    
    try {
      // Use REST endpoint to start background analysis
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analyze/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ url, topic: topic || null })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Analyse konnte nicht gestartet werden');
      }
      
      const data = await response.json();
      console.log('Analysis started:', data);
      
      // Save pending analysis with job_id for reliable polling
      setPendingAnalysis(url, data.job_id);
      toast.success('Analyse gestartet! Du wirst zum Verlauf weitergeleitet...');
      
      // Navigate to history immediately
      navigate('/history');
      
    } catch (err) {
      console.error('Failed to start analysis:', err);
      toast.error(err.message || 'Fehler beim Starten der Analyse');
    } finally {
      setIsStarting(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert!');
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* URL Input Card */}
      <Card className="mb-8" data-testid="url-input-card">
        <CardHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle className="text-xl">Video analysieren</CardTitle>
            <YouTubeLogo className="h-5 w-5" style={{ color: '#FF0000' }} />
            <InstagramLogo className="h-5 w-5" />
            <Badge variant="secondary" className="bg-accent/10 text-accent">
              KI-Analyse
            </Badge>
          </div>
          <CardDescription>
            Füge eine <strong>YouTube</strong> oder <strong>Instagram Reel</strong> URL ein - wir extrahieren <strong>alles automatisch</strong>: Titel, Beschreibung, Tags & Transkript!
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              placeholder="https://www.youtube.com/watch?v=... oder https://www.instagram.com/reel/..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              data-testid="url-input-field"
              className="flex-1 h-11"
              disabled={isStarting}
            />
            <Button
              onClick={handleAnalyze}
              disabled={isStarting || !url}
              data-testid="run-analysis-button"
              className="h-11 px-6"
            >
              {isStarting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Starte...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Analysieren
                </>
              )}
            </Button>
          </div>
          
          <Input
            placeholder="Optional: Kontext oder Thema des Videos"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            data-testid="topic-input-field"
            className="h-10"
            disabled={isStarting}
          />

          <p className="text-sm text-muted-foreground">
            Alles wird automatisch extrahiert: Caption, Hashtags & gesprochener Text.
          </p>

          {isAnalyzing && (
            <div className="flex items-center justify-center gap-3 p-6" data-testid="analysis-progress-bar">
              <div className="h-8 w-8 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
              <span className="text-lg font-medium text-foreground">Video wird analysiert...</span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive" data-testid="error-message">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loading State */}
      {isAnalyzing && <AnalysisSkeleton />}

      {/* Analysis Results */}
      {analysis && !isAnalyzing && (
        <div className="space-y-6 fade-in" data-testid="analysis-results">
          {/* Score & Overview */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-lg border bg-card">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-2xl font-bold text-primary">{analysis.virality_score}</span>
              </div>
              <div>
                <h3 className="font-semibold">Viralitäts-Score</h3>
                <p className="text-sm text-muted-foreground">{analysis.content_type}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {analysis.target_audience}
              </Badge>
            </div>
          </div>

          {/* Thumbnail Card */}
          {analysis.thumbnail?.url && (
            <ThumbnailCard 
              analysisId={analysis.id}
              thumbnail={analysis.thumbnail}
              onUpdate={(newThumbnail) => {
                // Analysis results are now handled in history page
              }}
            />
          )}

          {/* Retention/Pacing Chart */}
          {analysis.visual_analysis && (
            <RetentionChart 
              pacingData={analysis.visual_analysis.pacing_data}
              videoDuration={analysis.visual_analysis.video_duration}
              data-testid="retention-chart"
            />
          )}

          {/* Insight Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <InsightCard
              icon={Play}
              title="Hook Analyse"
              content={analysis.hook_analysis}
              badge="0-3 Sek"
              testId="insight-card-hook"
            />
            {/* Pacing & Schnitte - nur für Instagram */}
            {analysis.platform !== 'youtube' && analysis.pacing_notes && (
              <InsightCard
                icon={Scissors}
                title="Pacing & Schnitte"
                content={analysis.pacing_notes}
                testId="insight-card-pacing"
              />
            )}
            <InsightCard
              icon={Sparkles}
              title="Editing Stil"
              content={analysis.editing_style}
              testId="insight-card-editing"
            />
            {/* Caption Insights - nur für Instagram */}
            {analysis.platform !== 'youtube' && analysis.caption_insights && (
              <InsightCard
                icon={MessageSquare}
                title="Caption Insights"
                content={analysis.caption_insights}
                testId="insight-card-caption"
              />
            )}
            <InsightCard
              icon={Hash}
              title="Hashtag Analyse"
              content={analysis.hashtag_analysis}
              testId="insight-card-hashtags"
            />
            <InsightCard
              icon={Music}
              title="Audio Analyse"
              content={analysis.audio_analysis}
              testId="insight-card-audio"
            />
            {/* Transkript-Zusammenfassung - für YouTube und Instagram */}
            {analysis.transcript_summary && (
              <InsightCard
                icon={FileText}
                title="Transkript-Zusammenfassung"
                content={analysis.transcript_summary}
                testId="insight-card-transcript-summary"
              />
            )}
          </div>

          {/* VSL/Sales-Video Rhetorik-Analyse - für YouTube und Instagram wenn erkannt */}
          {analysis.is_sales_video && analysis.sales_framework && (
            <Card data-testid="sales-rhetoric-section" className="border-orange-500/30 bg-orange-500/5">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-orange-500" />
                  <CardTitle>VSL/Sales-Video Rhetorik</CardTitle>
                  <Badge variant="secondary" className="bg-orange-500/20 text-orange-600">{analysis.sales_framework}</Badge>
                </div>
                <CardDescription>
                  Erkanntes Verkaufs-Framework und rhetorische Phasen
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {analysis.sales_phases && analysis.sales_phases.length > 0 && (
                  <div className="space-y-3">
                    {analysis.sales_phases.map((phase, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-background border">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-600 font-bold text-sm">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-foreground">{phase.phase}</div>
                          {phase.approx_position && (
                            <div className="text-xs text-muted-foreground mb-1">{phase.approx_position}</div>
                          )}
                          <div className="text-sm text-muted-foreground">{phase.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Visual Analysis Section */}
          {analysis.visual_analysis && (
            <Card data-testid="visual-analysis-section" className="border-accent/30 bg-accent/5">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-accent" />
                  <CardTitle>Visuelle Analyse</CardTitle>
                  <Badge variant="secondary" className="bg-accent/20 text-accent">GPT-4o Vision</Badge>
                </div>
                <CardDescription>
                  KI-gestützte Analyse der visuellen Inhalte
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Visual Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 rounded-lg bg-background border text-center">
                    <div className="text-2xl font-bold text-accent">{analysis.visual_analysis.scene_count}</div>
                    <div className="text-xs text-muted-foreground">Szenen</div>
                  </div>
                  <div className="p-3 rounded-lg bg-background border text-center">
                    <div className="text-2xl font-bold text-accent">{analysis.visual_analysis.cuts_detected}</div>
                    <div className="text-xs text-muted-foreground">Schnitte</div>
                  </div>
                  <div className="col-span-2 p-3 rounded-lg bg-background border">
                    <div className="text-sm font-medium">{analysis.visual_analysis.cut_style}</div>
                    <div className="text-xs text-muted-foreground">Schnittstil</div>
                  </div>
                </div>

                {/* Visual Hook */}
                {analysis.visual_analysis.visual_hook && (
                  <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                    <div className="flex items-center gap-2 mb-1">
                      <Camera className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">Visueller Hook</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{analysis.visual_analysis.visual_hook}</p>
                  </div>
                )}

                {/* Scenes */}
                {analysis.visual_analysis.scenes?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Film className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Szenen-Breakdown</span>
                    </div>
                    <div className="space-y-2">
                      {analysis.visual_analysis.scenes.map((scene, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-2 rounded-lg bg-muted/30">
                          <Badge variant="outline" className="shrink-0">{scene.frame_range || `Szene ${idx + 1}`}</Badge>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm">{scene.description}</div>
                            {scene.type && (
                              <Badge variant="secondary" className="mt-1 text-xs">{scene.type}</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Visual Elements */}
                {analysis.visual_analysis.visual_elements?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Layers className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Visuelle Elemente</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {analysis.visual_analysis.visual_elements.map((elem, idx) => (
                        <Badge key={idx} variant="secondary">{elem}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Text on Screen */}
                {analysis.visual_analysis.text_on_screen?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Type className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Text im Video</span>
                    </div>
                    <div className="space-y-1">
                      {analysis.visual_analysis.text_on_screen.map((text, idx) => (
                        <div key={idx} className="text-sm p-2 rounded bg-muted/30 font-mono">{text}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Editing Techniques & Color Scheme */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {analysis.visual_analysis.editing_techniques?.length > 0 && (
                    <div className="p-3 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2 mb-2">
                        <MonitorPlay className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Editing-Techniken</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {analysis.visual_analysis.editing_techniques.map((tech, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{tech}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {analysis.visual_analysis.color_scheme && (
                    <div className="p-3 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Palette className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Farbschema</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{analysis.visual_analysis.color_scheme}</p>
                    </div>
                  )}
                </div>

                {/* Visual Quality */}
                {analysis.visual_analysis.visual_quality && (
                  <div className="p-3 rounded-lg bg-muted/30">
                    <div className="text-sm font-medium mb-1">Visuelle Qualität</div>
                    <p className="text-sm text-muted-foreground">{analysis.visual_analysis.visual_quality}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Download Error Warning */}
          {analysis.download_error && (
            <Card className="border-yellow-500/50 bg-yellow-500/5" data-testid="download-error-section">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-500" />
                  <CardTitle className="text-yellow-600 dark:text-yellow-400">Video-Download fehlgeschlagen</CardTitle>
                </div>
                <CardDescription>
                  {analysis.download_error}
                </CardDescription>
              </CardHeader>
            </Card>
          )}

          {/* Transcript Section */}
          {analysis.transcript && (
            <Card data-testid="transcript-section">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-primary" />
                    <CardTitle>Transkript</CardTitle>
                    <Badge variant="secondary">Whisper AI</Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(analysis.transcript)}
                    data-testid="copy-transcript-button"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <CardDescription>
                  Automatisch transkribierter gesprochener Text
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 rounded-lg bg-muted/30 border">
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{analysis.transcript}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Viral Factors */}
          {analysis.viral_factors?.length > 0 && (
            <Card data-testid="viral-factors-section">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  <CardTitle>Virale Faktoren</CardTitle>
                </div>
                <CardDescription>
                  Die wichtigsten Gründe, warum dieses Video viral geht
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {analysis.viral_factors.map((factor, index) => (
                    <ViralFactorCard key={index} factor={factor} index={index} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Key Takeaways */}
          {analysis.key_takeaways?.length > 0 && (
            <Card data-testid="takeaways-section">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-accent" />
                  <CardTitle>Key Takeaways</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {analysis.key_takeaways.map((takeaway, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{takeaway}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Generate Script Button */}
          <div className="flex justify-center">
            <Link to={`/script?analysis_id=${analysis.id}`}>
              <Button size="lg" className="h-12 px-8" data-testid="go-to-script-button">
                <FileText className="h-4 w-4 mr-2" />
                Script generieren
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!analysis && !isAnalyzing && (
        <div className="text-center py-16">
          <div className="h-16 w-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">Keine Analyse vorhanden</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Füge oben eine YouTube oder Instagram URL ein, um mit der Analyse zu beginnen.
          </p>
        </div>
      )}
    </div>
  );
};

// ============== Script Generator Page ==============
const ScriptPage = () => {
  const [topic, setTopic] = useState('');
  const [style, setStyle] = useState('engaging');
  const [tone, setTone] = useState('professional yet relatable');
  const [duration, setDuration] = useState(60);
  const [isGenerating, setIsGenerating] = useState(false);
  const [script, setScript] = useState(null);
  const [activeTab, setActiveTab] = useState('hook');

  // Get analysis_id from URL if present
  const searchParams = new URLSearchParams(window.location.search);
  const analysisId = searchParams.get('analysis_id');

  const handleGenerate = async () => {
    if (!topic) {
      toast.error('Bitte gib ein Thema ein');
      return;
    }

    setIsGenerating(true);

    try {
      const response = await axios.post(`${API}/generate-script`, {
        topic,
        style,
        tone,
        duration_seconds: duration,
        analysis_id: analysisId || null
      });
      setScript(response.data);
      toast.success('Script erfolgreich generiert!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Script-Generierung fehlgeschlagen';
      toast.error(errorMsg);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert!');
  };

  const handleCopyAll = () => {
    if (!script) return;
    const fullScript = `HOOK (${script.hook.duration_seconds}s):\n${script.hook.content}\n\nBODY (${script.body.duration_seconds}s):\n${script.body.content}\n\nCTA (${script.cta.duration_seconds}s):\n${script.cta.content}`;
    handleCopy(fullScript);
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Panel */}
        <div className="lg:col-span-1 space-y-4">
          <Card data-testid="script-input-card">
            <CardHeader>
              <CardTitle className="text-lg">Script Einstellungen</CardTitle>
              <CardDescription>
                Konfiguriere dein virales Script
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Thema *</label>
                <Textarea
                  placeholder="z.B. 5 Tipps für bessere Produktivität..."
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  data-testid="script-topic-input"
                  className="min-h-[100px]"
                  disabled={isGenerating}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Stil</label>
                <Input
                  placeholder="engaging, educational, entertaining..."
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  data-testid="script-style-input"
                  disabled={isGenerating}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Ton</label>
                <Input
                  placeholder="professional, casual, humorous..."
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  data-testid="script-tone-input"
                  disabled={isGenerating}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Dauer (Sekunden)</label>
                <Input
                  type="number"
                  min={15}
                  max={180}
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value) || 60)}
                  data-testid="script-duration-input"
                  disabled={isGenerating}
                />
              </div>

              {analysisId && (
                <div className="p-3 rounded-lg bg-primary/10 text-sm">
                  <p className="text-primary font-medium">Basiert auf Analyse</p>
                  <p className="text-muted-foreground text-xs mt-1">Das Script wird anhand der vorherigen Analyse optimiert.</p>
                </div>
              )}

              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !topic}
                className="w-full h-11"
                data-testid="generate-script-button"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generiere...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Script generieren
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Script Output */}
        <div className="lg:col-span-2">
          {isGenerating ? (
            <Card>
              <CardContent className="py-8">
                <div className="space-y-4">
                  <Skeleton className="h-8 w-48" />
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-8 w-32" />
                  <Skeleton className="h-48 w-full" />
                </div>
              </CardContent>
            </Card>
          ) : script ? (
            <Card data-testid="script-output-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Generiertes Script</CardTitle>
                    <CardDescription>
                      {script.total_duration_seconds} Sekunden • {script.tone}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyAll}
                    data-testid="copy-all-button"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Alles kopieren
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="w-full justify-start mb-4">
                    <TabsTrigger value="hook" data-testid="tab-hook">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-medium mr-2">1</span>
                      Hook
                    </TabsTrigger>
                    <TabsTrigger value="body" data-testid="tab-body">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-medium mr-2">2</span>
                      Body
                    </TabsTrigger>
                    <TabsTrigger value="cta" data-testid="tab-cta">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-medium mr-2">3</span>
                      CTA
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="hook" data-testid="script-section-hook">
                    <ScriptSectionCard
                      title="Hook"
                      duration={script.hook.duration_seconds}
                      content={script.hook.content}
                      notes={script.hook.notes}
                      onCopy={() => handleCopy(script.hook.content)}
                    />
                  </TabsContent>

                  <TabsContent value="body" data-testid="script-section-body">
                    <ScriptSectionCard
                      title="Body"
                      duration={script.body.duration_seconds}
                      content={script.body.content}
                      notes={script.body.notes}
                      onCopy={() => handleCopy(script.body.content)}
                    />
                  </TabsContent>

                  <TabsContent value="cta" data-testid="script-section-cta">
                    <ScriptSectionCard
                      title="Call-to-Action"
                      duration={script.cta.duration_seconds}
                      content={script.cta.content}
                      notes={script.cta.notes}
                      onCopy={() => handleCopy(script.cta.content)}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-16">
                <div className="text-center">
                  <div className="h-16 w-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
                    <FileText className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Kein Script vorhanden</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Gib links ein Thema ein und klicke auf "Script generieren".
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

const ScriptSectionCard = ({ title, duration, content, notes, onCopy }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <h4 className="font-semibold">{title}</h4>
        <Badge variant="secondary" className="text-xs">{duration}s</Badge>
      </div>
      <Button variant="ghost" size="sm" onClick={onCopy} data-testid="copy-section-button">
        <Copy className="h-4 w-4" />
      </Button>
    </div>
    <div className="p-4 rounded-lg bg-muted/30 border">
      <p className="text-sm whitespace-pre-wrap leading-relaxed">{content}</p>
    </div>
    {notes && (
      <div className="p-3 rounded-lg bg-accent/10 border border-accent/20">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-accent">Tipp:</span> {notes}
        </p>
      </div>
    )}
  </div>
);

// ============== History Page ==============

// Helper to get proxied image URL
const getProxiedImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  return `${API}/proxy-image?url=${encodeURIComponent(imageUrl)}`;
};

// Helper to manage pending analysis in localStorage
const getPendingAnalysis = () => {
  const pending = localStorage.getItem('pending_analysis');
  if (!pending) return null;
  
  try {
    const parsed = JSON.parse(pending);
    
    // Auto-expire after 10 minutes
    const TEN_MINUTES = 10 * 60 * 1000;
    if (Date.now() - parsed.startedAt > TEN_MINUTES) {
      localStorage.removeItem('pending_analysis');
      return null;
    }
    
    return parsed;
  } catch {
    localStorage.removeItem('pending_analysis');
    return null;
  }
};

const setPendingAnalysis = (url, jobId) => {
  if (url) {
    localStorage.setItem('pending_analysis', JSON.stringify({
      url,
      jobId: jobId || null,
      startedAt: Date.now(),
      lastProgress: 0
    }));
  } else {
    localStorage.removeItem('pending_analysis');
  }
};

const updatePendingProgress = (progress) => {
  const pending = localStorage.getItem('pending_analysis');
  if (!pending) return;
  try {
    const parsed = JSON.parse(pending);
    parsed.lastProgress = progress;
    localStorage.setItem('pending_analysis', JSON.stringify(parsed));
  } catch { /* ignore */ }
};

const HistoryPage = () => {
  const [analyses, setAnalyses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  
  // Pending analysis state — restore progress from localStorage to avoid visual reset on refresh
  const [pendingAnalysis, setPendingAnalysisState] = useState(getPendingAnalysis());
  const [pendingProgress, setPendingProgress] = useState(() => {
    const p = getPendingAnalysis();
    return p?.lastProgress || 0;
  });
  
  // Clear stale pending analysis on mount
  useEffect(() => {
    const pending = getPendingAnalysis();
    if (!pending) {
      setPendingAnalysisState(null);
    } else {
      setPendingAnalysisState(pending);
    }
  }, []);
  
  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  
  const fetchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/analyses?limit=20`);
      const analysesData = response.data || [];
      
      setAnalyses(prev => {
        if (analysesData.length === 0 && prev.length > 0) return prev;
        return analysesData;
      });
    } catch (err) {
      console.error('Fehler beim Laden des Verlaufs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Poll for pending analysis completion
  useEffect(() => {
    if (!pendingAnalysis) return;
    
    let cancelled = false;
    
    const checkProgress = async () => {
      if (cancelled) return;
      
      try {
        // If we have a job_id, use the dedicated status endpoint (most reliable)
        if (pendingAnalysis.jobId) {
          const statusRes = await axios.get(`${API}/analyze/status/${pendingAnalysis.jobId}`);
          const job = statusRes.data;
          
          if (cancelled) return;
          
          if (job.status === 'completed') {
            // Fetch the completed analyses list to show the result
            const analysesRes = await axios.get(`${API}/analyses?limit=20`);
            const normalizeUrl = (u) => u?.split('?')[0]?.replace(/\/+$/, '')?.toLowerCase() || '';
            const found = (analysesRes.data || []).find(a => 
              normalizeUrl(a.url) === normalizeUrl(pendingAnalysis.url) && a.status === 'completed'
            );
            
            setPendingAnalysis(null);
            setPendingAnalysisState(null);
            localStorage.removeItem('pending_analysis');
            setAnalyses(analysesRes.data || []);
            if (found) setSelectedAnalysis(found);
            toast.success('Analyse abgeschlossen!');
            return;
          }
          
          if (job.status === 'failed') {
            setPendingAnalysis(null);
            setPendingAnalysisState(null);
            localStorage.removeItem('pending_analysis');
            toast.error(job.progress_message || job.error_message || 'Analyse fehlgeschlagen.');
            return;
          }
          
          // Update progress from backend and persist to localStorage
          const realProgress = job.progress || 0;
          setPendingProgress(realProgress);
          updatePendingProgress(realProgress);
          return;
        }
        
        // Fallback: no job_id — match by URL (legacy pending entries)
        const normalizeUrl = (u) => u?.split('?')[0]?.replace(/\/+$/, '')?.toLowerCase() || '';
        const pendingNormalized = normalizeUrl(pendingAnalysis.url);
        
        // Check completed analyses
        const analysesRes = await axios.get(`${API}/analyses?limit=20`);
        const found = (analysesRes.data || []).find(a => 
          normalizeUrl(a.url) === pendingNormalized && a.status === 'completed'
        );
        
        if (found) {
          if (cancelled) return;
          setPendingAnalysis(null);
          setPendingAnalysisState(null);
          localStorage.removeItem('pending_analysis');
          setAnalyses(analysesRes.data);
          setSelectedAnalysis(found);
          toast.success('Analyse abgeschlossen!');
          return;
        }
        
        // Check active jobs
        const jobsRes = await axios.get(`${API}/jobs/active`);
        const matchingJob = (jobsRes.data || []).find(j => 
          normalizeUrl(j.url) === pendingNormalized
        );
        
        if (matchingJob) {
          if (matchingJob.status === 'failed') {
            if (cancelled) return;
            setPendingAnalysis(null);
            setPendingAnalysisState(null);
            localStorage.removeItem('pending_analysis');
            toast.error(matchingJob.progress_message || 'Analyse fehlgeschlagen.');
            return;
          }
          const realProgress = matchingJob.progress || 0;
          setPendingProgress(realProgress);
          updatePendingProgress(realProgress);
        } else {
          // Not in active jobs and not completed — might be saving, nudge progress
          setPendingProgress(prev => Math.min(prev + 2, 95));
        }
      } catch {
        // Network error — just keep waiting
      }
    };
    
    // Check immediately on mount
    checkProgress();
    
    // Then poll every 4 seconds
    const pollInterval = setInterval(checkProgress, 4000);
    
    // Timeout after 5 minutes
    const timeout = setTimeout(() => {
      setPendingAnalysis(null);
      setPendingAnalysisState(null);
      localStorage.removeItem('pending_analysis');
      toast.error('Analyse hat zu lange gedauert. Bitte prüfe den Verlauf.');
    }, 300000);
    
    return () => {
      cancelled = true;
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [pendingAnalysis]);

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const handleSelectAnalysis = async (analysis) => {
    // Check if we're on mobile (less than lg breakpoint = 1024px)
    const isMobile = window.innerWidth < 1024;
    
    // Don't reload if same analysis (toggle off on desktop)
    if (selectedAnalysis?.id === analysis.id && !isMobileSheetOpen) {
      setSelectedAnalysis(null);
      return;
    }
    
    setIsLoadingDetail(true);
    // Only open mobile sheet on mobile devices
    if (isMobile) {
      setIsMobileSheetOpen(true);
    }
    
    try {
      // Fetch full analysis details (cached from DB, no new request)
      const response = await axios.get(`${API}/analyses/${analysis.id}`);
      setSelectedAnalysis(response.data);
    } catch (err) {
      toast.error('Fehler beim Laden der Analyse');
      setIsMobileSheetOpen(false);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleDeleteAnalysis = async (analysisId) => {
    setDeletingId(analysisId);
    try {
      await axios.delete(`${API}/analyses/${analysisId}`);
      setAnalyses(prev => prev.filter(a => a.id !== analysisId));
      if (selectedAnalysis?.id === analysisId) {
        setSelectedAnalysis(null);
        setIsMobileSheetOpen(false);
      }
      toast.success('Analyse gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert!');
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold tracking-tight">Analyse Verlauf</h2>
        <p className="text-muted-foreground">Deine letzten Analysen - Klicke zum Öffnen</p>
      </div>

      {/* Pending Analysis Progress Card - ONLY show if analysis NOT yet visible in list at all */}
      {pendingAnalysis && !analyses.some(a => {
        const normalizeUrl = (u) => u?.split('?')[0]?.replace(/\/+$/, '')?.toLowerCase() || '';
        return normalizeUrl(a.url) === normalizeUrl(pendingAnalysis.url);
      }) && (
        <Card className="mb-6 border-primary/50 bg-primary/5" data-testid="pending-analysis-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full border-4 border-primary/20 border-t-primary animate-spin flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-foreground">Analyse läuft...</span>
                  <span className="text-sm font-semibold text-primary">{Math.round(pendingProgress)}%</span>
                </div>
                <Progress value={pendingProgress} className="h-2 mb-2" />
                <p className="text-sm text-muted-foreground truncate">{pendingAnalysis.url}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Analysis List */}
        <div className="lg:col-span-1 space-y-3">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-3">
                    <div className="flex gap-3">
                      <Skeleton className="h-16 w-16 rounded-lg flex-shrink-0" />
                      <div className="flex-1">
                        <Skeleton className="h-4 w-3/4 mb-2" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : analyses.length > 0 ? (
            <div className="space-y-3" data-testid="history-list">
              {analyses.map((analysis) => {
                const isSelected = selectedAnalysis?.id === analysis.id;
                const thumbnailUrl = analysis.thumbnail?.url || analysis.scraped_metadata?.thumbnail_url;
                
                return (
                  <Card 
                    key={analysis.id} 
                    className={`cursor-pointer transition-all duration-200 ${
                      isSelected 
                        ? 'ring-2 ring-primary bg-primary/5' 
                        : 'hover:bg-muted/50 card-hover'
                    }`}
                    onClick={() => handleSelectAnalysis(analysis)}
                    data-testid={`history-item-${analysis.id}`}
                  >
                    <CardContent className="p-3">
                      <div className="flex gap-3">
                        {/* Thumbnail Preview */}
                        <div className="h-16 w-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                          {thumbnailUrl ? (
                            <img 
                              src={getProxiedImageUrl(thumbnailUrl)} 
                              alt="Thumbnail"
                              className="h-full w-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                          ) : null}
                          <div className={`h-full w-full items-center justify-center ${thumbnailUrl ? 'hidden' : 'flex'}`}>
                            <Film className="h-6 w-6 text-muted-foreground" />
                          </div>
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {/* Platform Logo */}
                            <PlatformIcon platform={analysis.platform} className="h-4 w-4 flex-shrink-0" />
                            {/* Score Badge or Processing Indicator */}
                            {analysis.status === 'processing' ? (
                              <Badge 
                                variant="secondary" 
                                className="text-xs bg-primary/10 text-primary flex items-center gap-1"
                              >
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Läuft...
                              </Badge>
                            ) : analysis.status === 'failed' ? (
                              <Badge 
                                variant="secondary" 
                                className="text-xs bg-red-500/10 text-red-600 dark:text-red-400"
                              >
                                Fehler
                              </Badge>
                            ) : (
                              <Badge 
                                variant="secondary" 
                                className={`text-xs ${
                                  (analysis.viral_score || analysis.virality_score) >= 80 
                                    ? 'bg-green-500/10 text-green-600 dark:text-green-400' 
                                    : (analysis.viral_score || analysis.virality_score) >= 60 
                                    ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                                    : 'bg-muted'
                                }`}
                              >
                                {analysis.viral_score || analysis.virality_score || '-'}
                              </Badge>
                            )}
                            <span className="text-xs text-muted-foreground truncate">
                              {analysis.content_type}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {analysis.title || analysis.url}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(analysis.created_at).toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          {/* Share Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10"
                            onClick={() => {
                              setShareTarget(analysis);
                              setShareDialogOpen(true);
                            }}
                            data-testid={`share-analysis-${analysis.id}`}
                          >
                            <Share2 className="h-4 w-4" />
                          </Button>
                          {/* Shared indicator */}
                          {analysis.shared_with?.length > 0 && (
                            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                              {analysis.shared_with.length}
                            </Badge>
                          )}
                          {/* Delete Button with AlertDialog */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                disabled={deletingId === analysis.id}
                                data-testid={`delete-analysis-${analysis.id}`}
                              >
                                {deletingId === analysis.id ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Analyse löschen?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Möchtest du diese Analyse wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteAnalysis(analysis.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Löschen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          {/* Arrow */}
                          <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${
                            isSelected ? 'rotate-90' : ''
                          }`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                    <Clock className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <h3 className="font-medium mb-1">Noch keine Analysen</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Starte eine neue Analyse
                  </p>
                  <Link to="/">
                    <Button size="sm" data-testid="start-analysis-button">
                      <Zap className="h-4 w-4 mr-2" />
                      Neue Analyse
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Analysis Detail View - Desktop Only */}
        <div className="lg:col-span-2 hidden lg:block">
          {isLoadingDetail ? (
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <Skeleton className="h-6 w-1/3" />
                  <Skeleton className="h-40 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              </CardContent>
            </Card>
          ) : selectedAnalysis ? (
            <AnalysisDetailContent 
              analysis={selectedAnalysis} 
              onCopy={handleCopy}
              onUpdateThumbnail={(newThumbnail) => {
                setSelectedAnalysis(prev => ({ ...prev, thumbnail: newThumbnail }));
              }}
            />
          ) : (
            <Card>
              <CardContent className="py-16">
                <div className="text-center">
                  <div className="h-16 w-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
                    <Eye className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Analyse auswählen</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Wähle links eine Analyse aus, um die Details anzuzeigen.
                    Keine neue Anfrage nötig - alle Daten sind gespeichert.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Mobile Sheet for Analysis Details */}
      <Sheet open={isMobileSheetOpen} onOpenChange={setIsMobileSheetOpen}>
        <SheetContent side="bottom" className="h-[90vh] p-0 lg:hidden">
          <div className="h-full overflow-y-auto">
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Analyse Details</h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setIsMobileSheetOpen(false)}
                  data-testid="close-mobile-sheet"
                >
                  Schließen
                </Button>
              </div>
            </div>
            <div className="p-4">
              {isLoadingDetail ? (
                <div className="space-y-4">
                  <Skeleton className="h-6 w-1/3" />
                  <Skeleton className="h-40 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : selectedAnalysis ? (
                <AnalysisDetailContent 
                  analysis={selectedAnalysis} 
                  onCopy={handleCopy}
                  onUpdateThumbnail={(newThumbnail) => {
                    setSelectedAnalysis(prev => ({ ...prev, thumbnail: newThumbnail }));
                  }}
                />
              ) : null}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        itemId={shareTarget?.id}
        itemType="analyses"
        itemName={shareTarget?.title || shareTarget?.url || "Analyse"}
        sharedWith={shareTarget?.shared_with || []}
        onShareUpdate={fetchAnalyses}
      />
    </div>
  );
};

// Analysis Detail Content Component (reusable for desktop and mobile)
const AnalysisDetailContent = ({ analysis, onCopy, onUpdateThumbnail }) => {
  return (
    <div className="space-y-4" data-testid="analysis-detail-view">
      {/* Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-xl font-bold text-primary">{analysis.virality_score}</span>
              </div>
              <div>
                <h3 className="font-semibold">Viralitäts-Score</h3>
                <p className="text-sm text-muted-foreground">{analysis.content_type}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <a href={analysis.url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">
                  <Play className="h-4 w-4 mr-1" />
                  Original
                </Button>
              </a>
              <Link to={`/script?analysis_id=${analysis.id}`}>
                <Button size="sm">
                  <FileText className="h-4 w-4 mr-1" />
                  Script
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Thumbnail */}
      {(analysis.thumbnail?.url || analysis.scraped_metadata?.thumbnail_url) && (
        <ThumbnailCard 
          analysisId={analysis.id}
          thumbnail={analysis.thumbnail || { 
            url: analysis.scraped_metadata?.thumbnail_url,
            description: '',
            json_description: null
          }}
          onUpdate={onUpdateThumbnail}
        />
      )}

      {/* Retention Chart */}
      {analysis.visual_analysis && (
        <RetentionChart 
          pacingData={analysis.visual_analysis.pacing_data}
          videoDuration={analysis.visual_analysis.video_duration}
        />
      )}

      {/* Key Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InsightCard
          icon={Play}
          title="Hook Analyse"
          content={analysis.hook_analysis}
          badge="0-3 Sek"
        />
        {/* Pacing & Schnitte - nur für Instagram */}
        {analysis.platform !== 'youtube' && analysis.pacing_notes && (
          <InsightCard
            icon={Scissors}
            title="Pacing & Schnitte"
            content={analysis.pacing_notes}
          />
        )}
        {/* Caption Insights - nur für Instagram */}
        {analysis.platform !== 'youtube' && analysis.caption_insights && (
          <InsightCard
            icon={MessageSquare}
            title="Caption Insights"
            content={analysis.caption_insights}
          />
        )}
        <InsightCard
          icon={Hash}
          title="Hashtag Analyse"
          content={analysis.hashtag_analysis}
        />
        {/* Transkript-Zusammenfassung - für YouTube und Instagram */}
        {analysis.transcript_summary && (
          <InsightCard
            icon={FileText}
            title="Transkript-Zusammenfassung"
            content={analysis.transcript_summary}
          />
        )}
      </div>

      {/* VSL/Sales-Video Rhetorik-Analyse - für YouTube und Instagram wenn erkannt */}
      {analysis.is_sales_video && analysis.sales_framework && (
        <Card className="border-orange-500/30 bg-orange-500/5">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-orange-500" />
              <CardTitle className="text-base">VSL/Sales Rhetorik</CardTitle>
              <Badge variant="secondary" className="bg-orange-500/20 text-orange-600 text-xs">{analysis.sales_framework}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {analysis.sales_phases && analysis.sales_phases.map((phase, index) => (
              <div key={index} className="flex items-start gap-2 text-sm">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-600 font-bold text-xs">
                  {index + 1}
                </span>
                <div>
                  <span className="font-medium">{phase.phase}</span>
                  <span className="text-muted-foreground"> - {phase.description}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Transcript */}
      {analysis.transcript && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-primary" />
                Transkript
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => onCopy(analysis.transcript)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {analysis.transcript}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Key Takeaways */}
      {analysis.key_takeaways?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-accent" />
              Key Takeaways
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {analysis.key_takeaways.map((takeaway, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{takeaway}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== Style Library Page ==============
const StyleLibraryPage = () => {
  const navigate = useNavigate();
  const [styles, setStyles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newStyleForm, setNewStyleForm] = useState({
    channel_url: '',
    style_name: '',
    video_count: 15
  });
  const [pollingIds, setPollingIds] = useState([]);
  
  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  
  // Style Suggestion state
  const [suggestionMode, setSuggestionMode] = useState('direct'); // 'direct' or 'suggestion'
  const [suggestionForm, setSuggestionForm] = useState({
    channel_url: '',
    video_count: 15
  });
  const [isRequestingSuggestion, setIsRequestingSuggestion] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [pollingSuggestionIds, setPollingSuggestionIds] = useState([]);
  const [acceptStyleName, setAcceptStyleName] = useState('');
  
  // Editable suggestion state (for modifying before saving)
  const [editableSuggestion, setEditableSuggestion] = useState(null);
  const [newSuggestionColor, setNewSuggestionColor] = useState('#');

  // Fetch styles on mount
  useEffect(() => {
    fetchStyles();
  }, []);

  // Poll for analyzing styles
  useEffect(() => {
    if (pollingIds.length === 0) return;

    const pollInterval = setInterval(async () => {
      for (const styleId of pollingIds) {
        try {
          const response = await axios.get(`${API}/styles/${styleId}`);
          const updatedStyle = response.data;
          
          setStyles(prev => prev.map(s => s.id === styleId ? updatedStyle : s));
          
          if (updatedStyle.status === 'completed' || updatedStyle.status === 'failed') {
            setPollingIds(prev => prev.filter(id => id !== styleId));
            if (updatedStyle.status === 'completed') {
              toast.success(`Style "${updatedStyle.name}" fertig analysiert!`);
            } else {
              toast.error(`Style-Analyse fehlgeschlagen: ${updatedStyle.error_message}`);
            }
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [pollingIds]);

  const fetchStyles = async () => {
    try {
      const response = await axios.get(`${API}/styles`);
      const stylesData = response.data || [];
      
      // Don't replace existing styles with empty list during polling
      setStyles(prev => {
        if (stylesData.length === 0 && prev.length > 0) {
          console.log('Skipping empty styles update - keeping existing');
          return prev;
        }
        return stylesData;
      });
      
      // Start polling for any analyzing styles
      const analyzingStyles = (response.data || []).filter(s => s.status === 'analyzing' || s.status === 'pending');
      setPollingIds(analyzingStyles.map(s => s.id));
    } catch (err) {
      toast.error('Fehler beim Laden der Styles');
    } finally {
      setIsLoading(false);
    }
  };
  // Fetch full style details including individual_analyses for psychology data
  const fetchStyleDetails = async (styleId) => {
    try {
      const response = await axios.get(`${API}/styles/${styleId}`);
      setSelectedStyle(response.data);
    } catch (err) {
      console.error('Error fetching style details:', err);
      // Fallback to the style from the list
      const fallbackStyle = styles.find(s => s.id === styleId);
      if (fallbackStyle) setSelectedStyle(fallbackStyle);
    }
  };

  const handleCreateStyle = async (e) => {
    e.preventDefault();
    
    if (!newStyleForm.channel_url || !newStyleForm.style_name) {
      toast.error('Bitte fülle alle Felder aus');
      return;
    }

    setIsCreating(true);
    try {
      const response = await axios.post(`${API}/styles/analyze-channel`, newStyleForm);
      toast.success('Style-Analyse gestartet!');
      
      // Add to styles list and start polling
      const newStyle = {
        id: response.data.id,
        name: newStyleForm.style_name,
        channel_url: newStyleForm.channel_url,
        status: 'pending',
        progress: 0,
        sample_thumbnails: []
      };
      setStyles(prev => [newStyle, ...prev]);
      setPollingIds(prev => [...prev, response.data.id]);
      
      // Reset form
      setNewStyleForm({ channel_url: '', style_name: '', video_count: 15 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Starten der Analyse');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteStyle = async (styleId) => {
    try {
      await axios.delete(`${API}/styles/${styleId}`);
      setStyles(prev => prev.filter(s => s.id !== styleId));
      if (selectedStyle?.id === styleId) setSelectedStyle(null);
      toast.success('Style gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Fetch suggestions on mount
  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/styles/suggestions`);
      // Only update if we got valid data
      if (response.data && Array.isArray(response.data)) {
        setSuggestions(prev => {
          // Keep existing data if new data is empty
          if (response.data.length === 0 && prev.length > 0) {
            console.log('Keeping existing suggestions - new data is empty');
            return prev;
          }
          return response.data;
        });
        
        // Start polling for analyzing suggestions
        const analyzing = response.data.filter(s => s.status === 'analyzing' || s.status === 'pending');
        setPollingSuggestionIds(analyzing.map(s => s.id));
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      // Keep existing data on error - don't clear
    }
  };

  // Poll for suggestion progress
  useEffect(() => {
    if (pollingSuggestionIds.length === 0) return;

    const pollInterval = setInterval(async () => {
      for (const suggestionId of pollingSuggestionIds) {
        try {
          const response = await axios.get(`${API}/styles/suggestions/${suggestionId}`);
          const updated = response.data;
          
          setSuggestions(prev => prev.map(s => s.id === suggestionId ? updated : s));
          if (selectedSuggestion?.id === suggestionId) {
            setSelectedSuggestion(updated);
          }
          
          if (updated.status === 'completed' || updated.status === 'failed') {
            setPollingSuggestionIds(prev => prev.filter(id => id !== suggestionId));
            if (updated.status === 'completed') {
              toast.success('Style-Vorschlag fertig!');
            } else {
              toast.error(`Vorschlag fehlgeschlagen: ${updated.error_message}`);
            }
          }
        } catch (err) {
          console.error('Suggestion polling error:', err);
        }
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [pollingSuggestionIds]);

  const handleRequestSuggestion = async (e) => {
    e.preventDefault();
    
    if (!suggestionForm.channel_url) {
      toast.error('Bitte gib eine Kanal-URL ein');
      return;
    }

    setIsRequestingSuggestion(true);
    try {
      const response = await axios.post(`${API}/styles/suggest-from-channel`, {
        channel_url: suggestionForm.channel_url,
        video_count: suggestionForm.video_count
      });
      
      toast.success('Style-Vorschlag wird erstellt...');
      
      const newSuggestion = {
        id: response.data.id,
        channel_url: suggestionForm.channel_url,
        status: 'pending',
        progress: 0
      };
      setSuggestions(prev => [newSuggestion, ...prev]);
      setPollingSuggestionIds(prev => [...prev, response.data.id]);
      
      setSuggestionForm({ channel_url: '', video_count: 15 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen des Vorschlags');
    } finally {
      setIsRequestingSuggestion(false);
    }
  };

  const handleAcceptSuggestion = async () => {
    if (!editableSuggestion || !acceptStyleName) {
      toast.error('Bitte gib einen Namen für den Style ein');
      return;
    }

    try {
      // First update the suggestion with edited data
      await axios.put(`${API}/styles/suggestions/${editableSuggestion.id}`, {
        suggested_style: editableSuggestion.suggested_style
      });
      
      const response = await axios.post(
        `${API}/styles/suggestions/${editableSuggestion.id}/accept?style_name=${encodeURIComponent(acceptStyleName)}`
      );
      
      toast.success(`Style "${acceptStyleName}" erfolgreich erstellt!`);
      
      // Refresh styles list
      fetchStyles();
      
      // Close suggestion panel
      setSelectedSuggestion(null);
      setEditableSuggestion(null);
      setAcceptStyleName('');
      
      // Remove suggestion from list
      setSuggestions(prev => prev.filter(s => s.id !== editableSuggestion.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Akzeptieren des Vorschlags');
    }
  };

  const handleDeleteSuggestion = async (suggestionId) => {
    try {
      await axios.delete(`${API}/styles/suggestions/${suggestionId}`);
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      if (selectedSuggestion?.id === suggestionId) setSelectedSuggestion(null);
      toast.success('Vorschlag gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Initialize editable suggestion when selecting a completed suggestion
  useEffect(() => {
    if (selectedSuggestion && selectedSuggestion.status === 'completed') {
      setEditableSuggestion({
        ...selectedSuggestion,
        suggested_style: {
          ...selectedSuggestion.suggested_style,
          dominant_palette: [...(selectedSuggestion.suggested_style?.dominant_palette || [])],
          common_elements: [...(selectedSuggestion.suggested_style?.common_elements || [])]
        }
      });
    } else {
      setEditableSuggestion(null);
    }
  }, [selectedSuggestion]);

  // Edit suggestion functions
  const handleRemoveSuggestionColor = (colorHex) => {
    if (!editableSuggestion) return;
    setEditableSuggestion(prev => ({
      ...prev,
      suggested_style: {
        ...prev.suggested_style,
        dominant_palette: prev.suggested_style.dominant_palette.filter(c => c.hex !== colorHex)
      }
    }));
  };

  const handleAddSuggestionColor = () => {
    if (!editableSuggestion) return;
    const color = newSuggestionColor.trim().toUpperCase();
    if (!color || color === '#' || !/^#[0-9A-Fa-f]{6}$/.test(color)) {
      toast.error('Bitte gib eine gültige HEX-Farbe ein (z.B. #FF5500)');
      return;
    }
    if (editableSuggestion.suggested_style.dominant_palette.some(c => c.hex === color)) {
      toast.error('Diese Farbe ist bereits vorhanden');
      return;
    }
    setEditableSuggestion(prev => ({
      ...prev,
      suggested_style: {
        ...prev.suggested_style,
        dominant_palette: [...prev.suggested_style.dominant_palette, { name: 'Custom', hex: color }]
      }
    }));
    setNewSuggestionColor('#');
  };

  const handleRemoveSuggestionElement = (element) => {
    if (!editableSuggestion) return;
    setEditableSuggestion(prev => ({
      ...prev,
      suggested_style: {
        ...prev.suggested_style,
        common_elements: prev.suggested_style.common_elements.filter(e => e !== element)
      }
    }));
  };

  const handleUpdateSuggestionField = (field, value) => {
    if (!editableSuggestion) return;
    setEditableSuggestion(prev => ({
      ...prev,
      suggested_style: {
        ...prev.suggested_style,
        [field]: value
      }
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/15 text-green-600 dark:text-green-400';
      case 'analyzing': case 'pending': case 'processing': case 'running':
        return 'bg-amber-500/15 text-amber-600 dark:text-amber-400';
      case 'failed': return 'bg-destructive/15 text-destructive';
      default: return 'bg-muted';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Fertig';
      case 'analyzing': case 'processing': case 'running': return 'In Bearbeitung';
      case 'pending': return 'Wird gestartet...';
      case 'failed': return 'Fehlgeschlagen';
      default: return status;
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="style-library-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Palette className="h-6 w-6 text-primary" />
          Style Library
        </h1>
        <p className="text-muted-foreground mt-1">
          Analysiere YouTube-Kanäle und speichere deren Thumbnail-Stile
        </p>
      </div>

      {/* Create New Style Form - with Tabs */}
      <Card className="mb-6" data-testid="create-style-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Neuen Stil erstellen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={suggestionMode} onValueChange={setSuggestionMode} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="direct" className="flex items-center gap-2" data-testid="tab-direct-style">
                <Zap className="h-4 w-4" />
                Direkt analysieren
              </TabsTrigger>
              <TabsTrigger value="suggestion" className="flex items-center gap-2" data-testid="tab-style-suggestion">
                <Lightbulb className="h-4 w-4" />
                Style-Vorschlag
              </TabsTrigger>
            </TabsList>

            {/* Direct Analysis Tab */}
            <TabsContent value="direct" className="mt-0">
              <p className="text-sm text-muted-foreground mb-4">
                Gib eine YouTube-Kanal-URL ein und wir analysieren die Thumbnails
              </p>
              <form onSubmit={handleCreateStyle} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Kanal-URL</label>
                    <Input
                      placeholder="https://youtube.com/@MrBeast"
                      value={newStyleForm.channel_url}
                      onChange={(e) => setNewStyleForm(prev => ({ ...prev, channel_url: e.target.value }))}
                      disabled={isCreating}
                      data-testid="channel-url-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Stil-Name</label>
                    <Input
                      placeholder="MrBeast Style"
                      value={newStyleForm.style_name}
                      onChange={(e) => setNewStyleForm(prev => ({ ...prev, style_name: e.target.value }))}
                      disabled={isCreating}
                      data-testid="style-name-input"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Anzahl Videos</label>
                    <Input
                      type="number"
                      min={5}
                      max={25}
                      value={newStyleForm.video_count}
                      onChange={(e) => setNewStyleForm(prev => ({ ...prev, video_count: parseInt(e.target.value) || 15 }))}
                      disabled={isCreating}
                      className="w-24"
                      data-testid="video-count-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={isCreating}
                    className="mt-6"
                    data-testid="start-analysis-btn"
                  >
                    {isCreating ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Startet...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        Analyse starten
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </TabsContent>

            {/* Style Suggestion Tab */}
            <TabsContent value="suggestion" className="mt-0">
              <div className="p-3 bg-primary/5 rounded-lg border border-primary/20 mb-4">
                <p className="text-sm text-muted-foreground flex items-start gap-2">
                  <Lightbulb className="h-4 w-4 mt-0.5 text-primary" />
                  <span>
                    Das System analysiert deinen Kanal und macht einen <strong>intelligenten Style-Vorschlag</strong> basierend auf deinen Video-Themen und bestehenden Thumbnails. 
                    Du entscheidest dann, ob du den Vorschlag als Style speichern möchtest.
                  </span>
                </p>
              </div>
              <form onSubmit={handleRequestSuggestion} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Deine Kanal-URL</label>
                    <Input
                      placeholder="https://youtube.com/@DeinKanal"
                      value={suggestionForm.channel_url}
                      onChange={(e) => setSuggestionForm(prev => ({ ...prev, channel_url: e.target.value }))}
                      disabled={isRequestingSuggestion}
                      data-testid="suggestion-channel-url-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Anzahl Videos (max. 25)</label>
                    <Input
                      type="number"
                      min={5}
                      max={25}
                      value={suggestionForm.video_count}
                      onChange={(e) => setSuggestionForm(prev => ({ ...prev, video_count: Math.min(25, parseInt(e.target.value) || 15) }))}
                      disabled={isRequestingSuggestion}
                      className="w-24"
                      data-testid="suggestion-video-count-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  disabled={isRequestingSuggestion}
                  data-testid="request-suggestion-btn"
                >
                  {isRequestingSuggestion ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Analysiert...
                    </>
                  ) : (
                    <>
                      <Lightbulb className="h-4 w-4 mr-2" />
                      Style-Vorschlag anfordern
                    </>
                  )}
                </Button>
              </form>

              {/* Suggestions List */}
              {suggestions.length > 0 && (
                <div className="mt-6 space-y-3">
                  <h4 className="text-sm font-medium">Deine Vorschläge</h4>
                  {suggestions.map(suggestion => (
                    <div 
                      key={suggestion.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedSuggestion?.id === suggestion.id 
                          ? 'ring-2 ring-primary bg-primary/5' 
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => setSelectedSuggestion(suggestion)}
                      data-testid={`suggestion-${suggestion.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{suggestion.channel_name || 'Kanal wird analysiert...'}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-xs">{suggestion.channel_url}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className={getStatusColor(suggestion.status)}>
                            {suggestion.status === 'analyzing' && suggestion.progress > 0 
                              ? `${suggestion.progress}%` 
                              : getStatusText(suggestion.status)}
                          </Badge>
                          <AlertDialog>
                            <AlertDialogTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Vorschlag löschen?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Dieser Vorschlag wird unwiderruflich gelöscht.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteSuggestion(suggestion.id)}>
                                  Löschen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                      
                      {/* Show progress bar for analyzing */}
                      {(suggestion.status === 'analyzing' || suggestion.status === 'pending') && (
                        <div className="mt-2">
                          <Progress value={suggestion.progress || 0} className="h-1" />
                        </div>
                      )}
                      
                      {/* Show themes for completed */}
                      {suggestion.status === 'completed' && suggestion.video_themes?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {suggestion.video_themes.slice(0, 4).map((theme, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {theme}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Suggestion Detail Sheet - EDITABLE */}
      <Sheet open={!!selectedSuggestion && selectedSuggestion.status === 'completed'} onOpenChange={() => setSelectedSuggestion(null)}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              Style-Vorschlag bearbeiten
            </SheetTitle>
            <SheetDescription>
              Für {selectedSuggestion?.channel_name} - Passe den Vorschlag an bevor du speicherst
            </SheetDescription>
          </SheetHeader>
          
          {editableSuggestion && editableSuggestion.suggested_style && (
            <div className="space-y-5 mt-4">
              {/* Reasoning (read-only) */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <h4 className="text-sm font-medium mb-2">Warum dieser Style?</h4>
                <p className="text-sm text-muted-foreground">{editableSuggestion.style_reasoning_de}</p>
              </div>
              
              {/* Video Themes (read-only) */}
              {editableSuggestion.video_themes?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Erkannte Themen</h4>
                  <div className="flex flex-wrap gap-1">
                    {editableSuggestion.video_themes.map((theme, idx) => (
                      <Badge key={idx} variant="secondary">{theme}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* EDITABLE: Color Palette */}
              <div className="p-3 border rounded-lg">
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Palette className="h-4 w-4 text-primary" />
                  Farbpalette (bearbeitbar)
                </h4>
                
                {/* Current colors with remove button */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {editableSuggestion.suggested_style.dominant_palette?.map((color, idx) => (
                    <div key={idx} className="relative group">
                      <div 
                        className="w-12 h-12 rounded-lg border-2 shadow-sm cursor-pointer transition-transform hover:scale-105"
                        style={{ backgroundColor: color.hex }}
                        onClick={() => {navigator.clipboard.writeText(color.hex); toast.success(`${color.hex} kopiert!`);}}
                        title={`${color.name} - ${color.hex}`}
                      />
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemoveSuggestionColor(color.hex); }}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-destructive-foreground rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Farbe entfernen"
                      >
                        ×
                      </button>
                      <span className="text-xs text-muted-foreground text-center block mt-1 truncate w-12">{color.hex}</span>
                    </div>
                  ))}
                </div>
                
                {/* Add new color */}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      placeholder="#FF5500"
                      value={newSuggestionColor}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (!val.startsWith('#')) val = '#' + val;
                        setNewSuggestionColor(val.toUpperCase());
                      }}
                      maxLength={7}
                      className="font-mono text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddSuggestionColor();
                        }
                      }}
                    />
                  </div>
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    onClick={handleAddSuggestionColor}
                    disabled={!newSuggestionColor || newSuggestionColor === '#'}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* EDITABLE: Common Elements */}
              {editableSuggestion.suggested_style.common_elements?.length > 0 && (
                <div className="p-3 border rounded-lg">
                  <h4 className="text-sm font-medium mb-3">Typische Elemente (bearbeitbar)</h4>
                  <div className="flex flex-wrap gap-2">
                    {editableSuggestion.suggested_style.common_elements.map((element, idx) => (
                      <Badge key={idx} variant="secondary" className="flex items-center gap-1 pr-1">
                        {element}
                        <button
                          onClick={() => handleRemoveSuggestionElement(element)}
                          className="ml-1 hover:text-destructive"
                          title="Element entfernen"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* EDITABLE: Style Details */}
              <div className="p-3 border rounded-lg space-y-3">
                <h4 className="text-sm font-medium">Style-Details (bearbeitbar)</h4>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Layout</label>
                    <select 
                      value={editableSuggestion.suggested_style.typical_layout || ''}
                      onChange={(e) => handleUpdateSuggestionField('typical_layout', e.target.value)}
                      className="w-full h-9 px-2 rounded-md border bg-background text-sm"
                    >
                      <option value="">Flexibel</option>
                      <option value="centered">Zentriert</option>
                      <option value="rule-of-thirds">Drittel-Regel</option>
                      <option value="asymmetric">Asymmetrisch</option>
                      <option value="split">Geteilt</option>
                    </select>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Gesichter</label>
                    <select 
                      value={editableSuggestion.suggested_style.uses_faces ? 'true' : 'false'}
                      onChange={(e) => handleUpdateSuggestionField('uses_faces', e.target.value === 'true')}
                      className="w-full h-9 px-2 rounded-md border bg-background text-sm"
                    >
                      <option value="true">Ja</option>
                      <option value="false">Nein</option>
                    </select>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Text-Stil</label>
                    <Input
                      value={editableSuggestion.suggested_style.text_patterns?.typical_style || ''}
                      onChange={(e) => handleUpdateSuggestionField('text_patterns', { 
                        ...editableSuggestion.suggested_style.text_patterns, 
                        typical_style: e.target.value 
                      })}
                      placeholder="z.B. Bold Sans-Serif"
                      className="h-9 text-sm"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Farbstimmung</label>
                    <select 
                      value={editableSuggestion.suggested_style.color_mood || ''}
                      onChange={(e) => handleUpdateSuggestionField('color_mood', e.target.value)}
                      className="w-full h-9 px-2 rounded-md border bg-background text-sm"
                    >
                      <option value="">Nicht definiert</option>
                      <option value="warm">Warm</option>
                      <option value="cold">Kalt</option>
                      <option value="vibrant">Vibrant</option>
                      <option value="muted">Gedämpft</option>
                      <option value="high-contrast">High-Contrast</option>
                    </select>
                  </div>
                </div>
              </div>
              
              {/* Implementation Tips (read-only) */}
              {editableSuggestion.implementation_tips_de?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Umsetzungs-Tipps</h4>
                  <ul className="space-y-1">
                    {editableSuggestion.implementation_tips_de.map((tip, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground flex gap-2">
                        <span className="text-primary">•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Accept as Style */}
              <div className="pt-4 border-t space-y-3">
                <h4 className="text-sm font-medium">Als Style speichern</h4>
                <Input
                  placeholder="Style-Name eingeben..."
                  value={acceptStyleName}
                  onChange={(e) => setAcceptStyleName(e.target.value)}
                  data-testid="accept-style-name-input"
                />
                <Button 
                  onClick={handleAcceptSuggestion} 
                  className="w-full"
                  disabled={!acceptStyleName}
                  data-testid="accept-suggestion-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Angepassten Vorschlag als Style speichern
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Styles Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-32 w-full mb-3" />
                <Skeleton className="h-4 w-2/3 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : styles.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {styles.map(style => (
            <Card 
              key={style.id} 
              className={`cursor-pointer transition-all hover:shadow-md ${selectedStyle?.id === style.id ? 'ring-2 ring-primary' : ''} ${(style.status === 'analyzing' || style.status === 'pending') ? 'ring-2 ring-primary/50' : ''}`}
              onClick={() => {
                if (style.status === 'completed') {
                  navigate(`/styles/${style.id}`);
                } else {
                  fetchStyleDetails(style.id);
                }
              }}
              data-testid={`style-card-${style.id}`}
            >
              <CardContent className="p-4">
                {/* Thumbnail Preview */}
                <div className="aspect-video rounded-lg overflow-hidden bg-muted mb-3 grid grid-cols-3 gap-0.5">
                  {style.sample_thumbnails?.slice(0, 3).map((url, idx) => (
                    <img
                      key={idx}
                      src={`${API}/proxy-image?url=${encodeURIComponent(url)}`}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ))}
                  {(!style.sample_thumbnails || style.sample_thumbnails.length === 0) && (
                    <div className="col-span-3 flex items-center justify-center">
                      <Palette className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Style Info */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium truncate">{style.name}</h3>
                    <Badge variant="secondary" className={getStatusColor(style.status)}>
                      {getStatusText(style.status)}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <YouTubeLogo className="h-4 w-4" style={{ color: '#FF0000' }} />
                    <span className="truncate">{style.channel_name || style.channel_url}</span>
                  </div>

                  {/* Status info for active styles */}
                  {(style.status === 'analyzing' || style.status === 'pending') && (
                    <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                      <div className="flex items-center gap-2 justify-center">
                        <Loader2 className="h-3 w-3 animate-spin text-primary" />
                        <p className="text-xs text-primary font-medium">
                          In Bearbeitung — Browser kann geschlossen werden
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Stats for completed styles */}
                  {style.status === 'completed' && (
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{style.successful_analyses || 0} Thumbnails</span>
                      {style.aggregated_style?.dominant_palette?.length > 0 && (
                        <div className="flex items-center gap-1">
                          {style.aggregated_style.dominant_palette.slice(0, 4).map((color, idx) => (
                            <div
                              key={idx}
                              className="w-3 h-3 rounded-full border"
                              style={{ backgroundColor: color.hex }}
                              title={color.name}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-between items-center pt-2">
                    <div className="flex items-center gap-1">
                      {style.shared_with?.length > 0 && (
                        <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                          <Share2 className="h-3 w-3 mr-0.5" />
                          {style.shared_with.length}
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {/* Share button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShareTarget(style);
                          setShareDialogOpen(true);
                        }}
                        data-testid={`share-style-${style.id}`}
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                      {/* Delete button */}
                      <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`delete-style-${style.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Style löschen?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Möchtest du "{style.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteStyle(style.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Löschen
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                <Palette className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="font-medium mb-1">Noch keine Styles</h3>
              <p className="text-sm text-muted-foreground">
                Analysiere deinen ersten YouTube-Kanal oben
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Style Detail Sheet */}
      {selectedStyle && selectedStyle.status === 'completed' && selectedStyle.aggregated_style && (
        <Sheet open={!!selectedStyle} onOpenChange={() => setSelectedStyle(null)}>
          <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
            <div className="space-y-6 py-4">
              <div>
                <h2 className="text-xl font-bold">{selectedStyle.name}</h2>
                <p className="text-muted-foreground flex items-center gap-2">
                  <YouTubeLogo className="h-4 w-4" style={{ color: '#FF0000' }} />
                  {selectedStyle.channel_name}
                </p>
              </div>

              {/* Dominant Colors */}
              {selectedStyle.aggregated_style.dominant_palette?.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Palette className="h-4 w-4 text-primary" />
                    Dominante Farben
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedStyle.aggregated_style.dominant_palette.map((color, idx) => (
                      <Tooltip key={idx}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(color.hex);
                              toast.success(`${color.hex} kopiert!`);
                            }}
                            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-muted/50"
                          >
                            <div
                              className="w-10 h-10 rounded-lg border shadow-sm"
                              style={{ backgroundColor: color.hex }}
                            />
                            <span className="text-xs font-mono">{color.hex}</span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>{color.name}</TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                  {selectedStyle.aggregated_style.color_mood && (
                    <p className="text-sm text-muted-foreground mt-2">
                      {selectedStyle.aggregated_style.color_mood}
                    </p>
                  )}
                </div>
              )}

              {/* Style Summary */}
              {selectedStyle.aggregated_style.style_summary_de && (
                <div>
                  <h3 className="font-medium mb-2">Stil-Beschreibung</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedStyle.aggregated_style.style_summary_de}
                  </p>
                </div>
              )}

              {/* Recreation Tips */}
              {selectedStyle.aggregated_style.recreation_tips_de?.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-accent" />
                    Nachbauen-Tipps
                  </h3>
                  <ul className="space-y-2">
                    {selectedStyle.aggregated_style.recreation_tips_de.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Common Elements */}
              {selectedStyle.aggregated_style.common_elements?.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2">Häufige Elemente</h3>
                  <div className="flex flex-wrap gap-1">
                    {selectedStyle.aggregated_style.common_elements.map((elem, idx) => (
                      <Badge key={idx} variant="outline">{elem}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* === PSYCHOLOGIE-DATEN (aus individual_analyses) === */}
              {selectedStyle.individual_analyses?.length > 0 && (() => {
                // Aggregiere Psychologie-Daten aus allen Analysen
                const allCognitiveBiases = [];
                const allDopamineTriggers = [];
                const allAlwaysPresent = [];
                const allNeverPresent = [];
                const allScrollStopPowers = [];
                const allViralFormulas = [];
                
                selectedStyle.individual_analyses.forEach(ia => {
                  const sa = ia.style_analysis || {};
                  const psych = sa.psychology || {};
                  const viral = sa.viral_formula || {};
                  
                  if (psych.cognitive_biases) allCognitiveBiases.push(...psych.cognitive_biases);
                  if (psych.dopamine_triggers) allDopamineTriggers.push(...psych.dopamine_triggers);
                  if (psych.scroll_stop_power) allScrollStopPowers.push(psych.scroll_stop_power);
                  if (viral.always_present) allAlwaysPresent.push(...viral.always_present);
                  if (viral.never_present) allNeverPresent.push(...viral.never_present);
                  if (viral.formula_pattern) allViralFormulas.push(viral.formula_pattern);
                });

                // Unique values
                const uniqueBiases = [...new Set(allCognitiveBiases)];
                const uniqueTriggers = [...new Set(allDopamineTriggers)];
                const uniqueAlways = [...new Set(allAlwaysPresent)];
                const uniqueNever = [...new Set(allNeverPresent)];

                return (
                  <>
                    {/* Psychology Section */}
                    {(uniqueBiases.length > 0 || uniqueTriggers.length > 0) && (
                      <div className="border-t pt-4">
                        <h3 className="font-medium mb-3 flex items-center gap-2">
                          <Brain className="h-4 w-4 text-purple-500" />
                          🧠 Psychologie-Analyse
                        </h3>
                        
                        {uniqueBiases.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-muted-foreground mb-1">Kognitive Biases:</p>
                            <div className="flex flex-wrap gap-1">
                              {uniqueBiases.map((bias, idx) => (
                                <Badge key={idx} variant="secondary" className="bg-purple-500/10 text-purple-700 dark:text-purple-300">
                                  {bias}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {uniqueTriggers.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-muted-foreground mb-1">Dopamin-Trigger:</p>
                            <div className="flex flex-wrap gap-1">
                              {uniqueTriggers.map((trigger, idx) => (
                                <Badge key={idx} variant="secondary" className="bg-orange-500/10 text-orange-700 dark:text-orange-300">
                                  {trigger}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {allScrollStopPowers.length > 0 && (
                          <div className="mb-2">
                            <p className="text-xs font-medium text-muted-foreground">
                              Scroll-Stop-Power: <span className="text-foreground font-bold">
                                {allScrollStopPowers.filter(p => p === 'high' || p === 'extreme').length}/{allScrollStopPowers.length} hoch
                              </span>
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Viral Formula Section */}
                    {(uniqueAlways.length > 0 || uniqueNever.length > 0 || allViralFormulas.length > 0) && (
                      <div className="border-t pt-4">
                        <h3 className="font-medium mb-3 flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-yellow-500" />
                          🔥 Virale Formel
                        </h3>
                        
                        {allViralFormulas.length > 0 && (
                          <div className="mb-3 p-2 bg-yellow-500/10 rounded-lg">
                            <p className="text-sm font-medium">{allViralFormulas[0]}</p>
                          </div>
                        )}
                        
                        {uniqueAlways.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-green-600 mb-1">✅ IMMER vorhanden:</p>
                            <div className="flex flex-wrap gap-1">
                              {uniqueAlways.map((elem, idx) => (
                                <Badge key={idx} variant="outline" className="border-green-500 text-green-700 dark:text-green-300">
                                  {elem}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {uniqueNever.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-red-600 mb-1">❌ NIE vorhanden:</p>
                            <div className="flex flex-wrap gap-1">
                              {uniqueNever.map((elem, idx) => (
                                <Badge key={idx} variant="outline" className="border-red-500 text-red-700 dark:text-red-300">
                                  {elem}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Stats */}
              <div className="text-xs text-muted-foreground border-t pt-4">
                {selectedStyle.successful_analyses} Thumbnails analysiert • 
                Erstellt am {new Date(selectedStyle.created_at).toLocaleDateString('de-DE')}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        itemId={shareTarget?.id}
        itemType="styles"
        itemName={shareTarget?.name || "Style"}
        sharedWith={shareTarget?.shared_with || []}
        onShareUpdate={fetchStyles}
      />
    </div>
  );
};

// ============== Thumbnail Generator Page ==============
const ThumbnailGeneratorPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [styles, setStyles] = useState([]);
  const [selectedStyleId, setSelectedStyleId] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [description, setDescription] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedThumbnails, setGeneratedThumbnails] = useState([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  
  // New state for Video-URL mode
  const [generatorMode, setGeneratorMode] = useState('manual'); // 'manual' or 'video-url'
  const [videoUrl, setVideoUrl] = useState('');
  const [useSmartRandom, setUseSmartRandom] = useState(false);
  const [detectedPlatform, setDetectedPlatform] = useState(null);
  
  // Brand Colors state
  const [brandColors, setBrandColors] = useState([]);
  const [newBrandColor, setNewBrandColor] = useState('#');
  
  // Variations state
  const [variationsCount, setVariationsCount] = useState(1);
  const [generatedVariations, setGeneratedVariations] = useState([]);
  
  // Background mode state
  const [useBackgroundMode, setUseBackgroundMode] = useState(true);
  
  // Persons state
  const [persons, setPersons] = useState([]);
  const [selectedPersons, setSelectedPersons] = useState([]);

  // Detect platform from URL
  useEffect(() => {
    if (!videoUrl) {
      setDetectedPlatform(null);
      return;
    }
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      setDetectedPlatform('youtube');
    } else if (videoUrl.includes('instagram.com')) {
      setDetectedPlatform('instagram');
    } else {
      setDetectedPlatform(null);
    }
  }, [videoUrl]);

  // Fetch styles on mount + optional reuse from job
  useEffect(() => {
    const fetchStyles = async () => {
      try {
        const response = await axios.get(`${API}/styles`);
        const completedStyles = response.data.filter(s => s.status === 'completed');
        setStyles(completedStyles);
        if (completedStyles.length > 0) {
          setSelectedStyleId(completedStyles[0].id);
        }

        // Reuse from job
        const reuseJobId = searchParams.get("reuse");
        if (reuseJobId) {
          try {
            const jobRes = await axios.get(`${API}/jobs/${reuseJobId}/wizard-data`);
            const d = jobRes.data?.input_data || {};
            if (d.video_title) setVideoTitle(d.video_title);
            if (d.description) setDescription(d.description);
            if (d.custom_prompt) setCustomPrompt(d.custom_prompt);
            if (d.style_id && completedStyles.some(s => s.id === d.style_id)) setSelectedStyleId(d.style_id);
            if (d.brand_colors?.length) setBrandColors(d.brand_colors);
            if (d.variations) setVariationsCount(d.variations);
            if (d.selected_persons?.length) setSelectedPersons(d.selected_persons);
            toast.success("Einstellungen vom vorherigen Auftrag geladen!");
          } catch (e) { console.error("Failed to load job reuse data:", e); }
        }
      } catch (err) {
        toast.error('Fehler beim Laden der Styles');
      } finally {
        setIsLoading(false);
      }
    };
    fetchStyles();
    fetchGeneratedThumbnails();
    fetchPersons();
  }, []);

  const fetchGeneratedThumbnails = async () => {
    try {
      const response = await axios.get(`${API}/thumbnails?limit=10`);
      setGeneratedThumbnails(response.data);
    } catch (err) {
      console.error('Error fetching thumbnails:', err);
    }
  };
  
  const fetchPersons = async () => {
    try {
      const response = await axios.get(`${API}/persons`);
      setPersons(response.data);
    } catch (err) {
      console.error('Error fetching persons:', err);
    }
  };
  
  const togglePersonSelection = (personId) => {
    setSelectedPersons(prev => 
      prev.includes(personId) 
        ? prev.filter(id => id !== personId)
        : [...prev, personId]
    );
  };

  // Brand Color handlers
  const handleAddBrandColor = () => {
    const color = newBrandColor.trim();
    if (color && /^#[0-9A-Fa-f]{6}$/.test(color) && !brandColors.includes(color)) {
      setBrandColors(prev => [...prev, color]);
      setNewBrandColor('#');
    } else if (!color || color === '#') {
      // Do nothing for empty
    } else {
      toast.error('Bitte gib eine gültige HEX-Farbe ein (z.B. #FF5500)');
    }
  };

  const handleRemoveBrandColor = (color) => {
    setBrandColors(prev => prev.filter(c => c !== color));
  };

  // Manual mode generation (existing)
  const handleGenerateManual = async () => {
    if (!selectedStyleId || !videoTitle) {
      toast.error('Bitte wähle einen Style und gib einen Video-Titel ein');
      return;
    }

    setIsGenerating(true);
    setGeneratedVariations([]);
    
    try {
      if (useBackgroundMode) {
        // Use job endpoint for background processing
        const response = await axios.post(`${API}/jobs/thumbnail`, {
          style_id: selectedStyleId,
          video_title: videoTitle,
          description: description,
          custom_prompt: customPrompt,
          brand_colors: brandColors.length > 0 ? brandColors : undefined,
          variations: variationsCount,
          selected_persons: selectedPersons.length > 0 ? selectedPersons : undefined
        });
        
        toast.success('Auftrag gestartet! Du kannst die Seite verlassen.', {
          action: {
            label: 'Zu Aufträgen',
            onClick: () => navigate('/jobs')
          }
        });
        setIsGenerating(false);
        return;
      }
      
      // Direct generation (foreground)
      const response = await axios.post(`${API}/thumbnails/generate`, {
        style_id: selectedStyleId,
        video_title: videoTitle,
        description: description,
        custom_prompt: customPrompt,
        brand_colors: brandColors.length > 0 ? brandColors : undefined,
        variations: variationsCount,
        selected_persons: selectedPersons.length > 0 ? selectedPersons : undefined
      });

      const { thumbnails, variations_generated } = response.data;
      
      toast.success(`${variations_generated} Thumbnail(s) erfolgreich generiert!`);
      
      // Fetch all generated thumbnails with image data
      const thumbsWithData = await Promise.all(
        thumbnails.map(async (t) => {
          const thumbResponse = await axios.get(`${API}/thumbnails/${t.id}`);
          return thumbResponse.data;
        })
      );
      
      setGeneratedVariations(thumbsWithData);
      
      // Select first one
      if (thumbsWithData.length > 0) {
        setSelectedThumbnail(thumbsWithData[0]);
      }
      
      // Refresh list
      fetchGeneratedThumbnails();
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler bei der Generierung');
    } finally {
      setIsGenerating(false);
    }
  };

  // Video-URL mode generation (new)
  const handleGenerateFromUrl = async () => {
    if (!videoUrl) {
      toast.error('Bitte gib eine Video-URL ein');
      return;
    }
    if (!detectedPlatform) {
      toast.error('Ungültige URL. Unterstützt werden YouTube und Instagram.');
      return;
    }
    if (!useSmartRandom && !selectedStyleId) {
      toast.error('Bitte wähle einen Style oder aktiviere Smart Random');
      return;
    }

    setIsGenerating(true);
    try {
      if (useBackgroundMode) {
        // Use job endpoint for background processing
        const response = await axios.post(`${API}/jobs/video-thumbnail`, {
          video_url: videoUrl,
          style_id: useSmartRandom ? null : selectedStyleId,
          use_smart_random: useSmartRandom,
          custom_prompt: customPrompt,
          brand_colors: brandColors.length > 0 ? brandColors : undefined,
          selected_persons: selectedPersons.length > 0 ? selectedPersons : undefined,
          variations: variationsCount
        });
        
        toast.success('Auftrag gestartet! Du kannst die Seite verlassen.', {
          action: {
            label: 'Zu Aufträgen',
            onClick: () => navigate('/jobs')
          }
        });
        setIsGenerating(false);
        return;
      }
      
      // Direct generation (foreground)
      const response = await axios.post(`${API}/thumbnails/generate-from-video`, {
        video_url: videoUrl,
        style_id: useSmartRandom ? null : selectedStyleId,
        use_smart_random: useSmartRandom,
        custom_prompt: customPrompt,
        brand_colors: brandColors.length > 0 ? brandColors : undefined,
        selected_persons: selectedPersons.length > 0 ? selectedPersons : undefined,
        variations: variationsCount
      });

      toast.success(`Thumbnail für "${response.data.video_title}" generiert!`);
      
      // Fetch the generated thumbnail with image data
      const thumbResponse = await axios.get(`${API}/thumbnails/${response.data.id}`);
      setSelectedThumbnail(thumbResponse.data);
      
      // Refresh list
      fetchGeneratedThumbnails();
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler bei der Generierung');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerate = () => {
    if (generatorMode === 'manual') {
      handleGenerateManual();
    } else {
      handleGenerateFromUrl();
    }
  };

  const handleDeleteThumbnail = async (thumbnailId) => {
    try {
      await axios.delete(`${API}/thumbnails/${thumbnailId}`);
      setGeneratedThumbnails(prev => prev.filter(t => t.id !== thumbnailId));
      if (selectedThumbnail?.id === thumbnailId) {
        setSelectedThumbnail(null);
      }
      toast.success('Thumbnail gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleSelectThumbnail = async (thumbnail) => {
    try {
      const response = await axios.get(`${API}/thumbnails/${thumbnail.id}`);
      setSelectedThumbnail(response.data);
    } catch (err) {
      toast.error('Fehler beim Laden des Thumbnails');
    }
  };

  const handleDownload = () => {
    if (!selectedThumbnail?.image_data) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${selectedThumbnail.image_data}`;
    link.download = `thumbnail-${selectedThumbnail.video_title.replace(/\s+/g, '-')}.png`;
    link.click();
    toast.success('Download gestartet!');
  };

  const selectedStyle = styles.find(s => s.id === selectedStyleId);
  
  // Check if generate button should be enabled
  const canGenerate = generatorMode === 'manual' 
    ? (selectedStyleId && videoTitle)
    : (videoUrl && detectedPlatform && (useSmartRandom || selectedStyleId));

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="thumbnail-generator-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          Thumbnail Generator
        </h1>
        <p className="text-muted-foreground mt-1">
          Generiere Thumbnails im Stil deiner gespeicherten Vorlagen
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Generator Form */}
        <div className="space-y-6">
          <Card data-testid="generator-form-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Neues Thumbnail erstellen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Mode Tabs */}
              <Tabs value={generatorMode} onValueChange={setGeneratorMode} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="manual" className="flex items-center gap-2" data-testid="tab-manual">
                    <Edit3 className="h-4 w-4" />
                    Freies Schreiben
                  </TabsTrigger>
                  <TabsTrigger value="video-url" className="flex items-center gap-2" data-testid="tab-video-url">
                    <LinkIcon className="h-4 w-4" />
                    Video-URL
                  </TabsTrigger>
                </TabsList>

                {/* Manual Mode Content */}
                <TabsContent value="manual" className="mt-4 space-y-4">
                  {/* Style Selector */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Style auswählen</label>
                    {isLoading ? (
                      <Skeleton className="h-10 w-full" />
                    ) : styles.length > 0 ? (
                      <select
                        value={selectedStyleId}
                        onChange={(e) => setSelectedStyleId(e.target.value)}
                        className="w-full h-10 px-3 rounded-md border bg-background text-sm"
                        data-testid="style-selector"
                      >
                        {styles.map(style => (
                          <option key={style.id} value={style.id}>
                            {style.name} ({style.channel_name})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-md">
                        Keine Styles verfügbar. 
                        <Link to="/styles" className="text-primary ml-1 hover:underline">
                          Erstelle zuerst einen Style
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Selected Style Preview */}
                  {selectedStyle && (
                    <div className="p-3 bg-muted/30 rounded-md space-y-2">
                      <div className="flex items-center gap-2">
                        <YouTubeLogo className="h-4 w-4" style={{ color: '#FF0000' }} />
                        <span className="text-sm font-medium">{selectedStyle.name}</span>
                      </div>
                      {selectedStyle.aggregated_style?.dominant_palette && (
                        <div className="flex items-center gap-1">
                          {selectedStyle.aggregated_style.dominant_palette.slice(0, 5).map((color, idx) => (
                            <div
                              key={idx}
                              className="w-4 h-4 rounded border"
                              style={{ backgroundColor: color.hex }}
                              title={color.name}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Video Title */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Video-Titel *</label>
                    <Input
                      placeholder="z.B. Die 5 besten Tipps für..."
                      value={videoTitle}
                      onChange={(e) => setVideoTitle(e.target.value)}
                      disabled={isGenerating}
                      data-testid="video-title-input"
                    />
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Beschreibung (optional)</label>
                    <Textarea
                      placeholder="Worum geht es im Video?"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      disabled={isGenerating}
                      rows={2}
                      data-testid="description-input"
                    />
                  </div>
                </TabsContent>

                {/* Video-URL Mode Content */}
                <TabsContent value="video-url" className="mt-4 space-y-4">
                  {/* Video URL Input */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Video-URL *</label>
                    <div className="relative">
                      <Input
                        placeholder="https://youtube.com/watch?v=... oder instagram.com/reel/..."
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        disabled={isGenerating}
                        className="pr-10"
                        data-testid="video-url-input"
                      />
                      {detectedPlatform && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {detectedPlatform === 'youtube' ? (
                            <YouTubeLogo className="h-5 w-5" style={{ color: '#FF0000' }} />
                          ) : (
                            <InstagramLogo className="h-5 w-5" />
                          )}
                        </div>
                      )}
                    </div>
                    {videoUrl && !detectedPlatform && (
                      <p className="text-xs text-destructive">
                        Ungültige URL. Unterstützt werden YouTube und Instagram URLs.
                      </p>
                    )}
                  </div>

                  {/* Smart Random Toggle */}
                  <div className="p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg border border-primary/20">
                    <div className="flex items-start gap-3">
                      <div 
                        className={`w-12 h-6 rounded-full cursor-pointer transition-colors flex items-center ${
                          useSmartRandom ? 'bg-primary justify-end' : 'bg-muted justify-start'
                        }`}
                        onClick={() => setUseSmartRandom(!useSmartRandom)}
                        data-testid="smart-random-toggle"
                      >
                        <div className="w-5 h-5 bg-white rounded-full m-0.5 shadow-sm" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-primary" />
                          <span className="font-medium text-sm">🎲 Smart Random</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          System analysiert alle Styles und erstellt den optimalen Scroll-Stopper für dein Video
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Style Selector (only if not Smart Random) */}
                  {!useSmartRandom && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Style auswählen</label>
                      {isLoading ? (
                        <Skeleton className="h-10 w-full" />
                      ) : styles.length > 0 ? (
                        <select
                          value={selectedStyleId}
                          onChange={(e) => setSelectedStyleId(e.target.value)}
                          className="w-full h-10 px-3 rounded-md border bg-background text-sm"
                          data-testid="style-selector-url"
                        >
                          {styles.map(style => (
                            <option key={style.id} value={style.id}>
                              {style.name} ({style.channel_name})
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-md">
                          Keine Styles verfügbar. Aktiviere Smart Random oder 
                          <Link to="/styles" className="text-primary ml-1 hover:underline">
                            erstelle einen Style
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {/* Custom Prompt and Brand Colors (for both modes) */}
              <Collapsible defaultOpen={brandColors.length > 0}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-start p-0 h-auto">
                    <ChevronRight className="h-4 w-4 mr-1" />
                    <span className="text-sm text-muted-foreground">Erweiterte Optionen & Marken-Farben</span>
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-3 space-y-4">
                  {/* Brand Colors */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Palette className="h-4 w-4 text-primary" />
                      Marken-Farben (optional)
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Deine Logo-Farben werden in den Style integriert
                    </p>
                    
                    {/* Color display */}
                    {brandColors.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {brandColors.map((color, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center gap-1 bg-muted/50 rounded-full pl-1 pr-2 py-1"
                          >
                            <div 
                              className="w-5 h-5 rounded-full border shadow-sm"
                              style={{ backgroundColor: color }}
                            />
                            <span className="text-xs font-mono">{color}</span>
                            <button
                              onClick={() => handleRemoveBrandColor(color)}
                              className="ml-1 text-muted-foreground hover:text-destructive"
                              type="button"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add color input */}
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          placeholder="#FF5500"
                          value={newBrandColor}
                          onChange={(e) => {
                            let val = e.target.value;
                            if (!val.startsWith('#')) val = '#' + val;
                            setNewBrandColor(val.toUpperCase());
                          }}
                          maxLength={7}
                          className="font-mono pr-10"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddBrandColor();
                            }
                          }}
                          data-testid="brand-color-input"
                        />
                        {newBrandColor && newBrandColor !== '#' && /^#[0-9A-Fa-f]{6}$/.test(newBrandColor) && (
                          <div 
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded border"
                            style={{ backgroundColor: newBrandColor }}
                          />
                        )}
                      </div>
                      <Button 
                        type="button"
                        variant="outline" 
                        size="sm"
                        onClick={handleAddBrandColor}
                        disabled={!newBrandColor || newBrandColor === '#' || brandColors.length >= 5}
                        data-testid="add-brand-color-btn"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {brandColors.length >= 5 && (
                      <p className="text-xs text-muted-foreground">Max. 5 Farben</p>
                    )}
                  </div>

                  {/* Custom Prompt */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Zusätzliche Anweisungen</label>
                    <Textarea
                      placeholder="z.B. Person mit blonden Haaren, Hintergrund mit Bergen..."
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      disabled={isGenerating}
                      rows={2}
                      data-testid="custom-prompt-input"
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Variations Selector */}
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium">Anzahl Varianten</label>
                    <p className="text-xs text-muted-foreground">Mehr Varianten = mehr Auswahl</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4].map(num => (
                      <button
                        key={num}
                        onClick={() => setVariationsCount(num)}
                        className={`w-9 h-9 rounded-md text-sm font-medium transition-all ${
                          variationsCount === num 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted hover:bg-muted-foreground/20'
                        }`}
                        disabled={isGenerating}
                        data-testid={`variation-${num}`}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Persons Selection */}
              {persons.length > 0 && (
                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="text-sm font-medium flex items-center gap-2">
                        <Users className="h-4 w-4 text-primary" />
                        Personen hinzufügen
                      </label>
                      <p className="text-xs text-muted-foreground">Optional: Personen ins Thumbnail einfügen</p>
                    </div>
                    <Link to="/persons" className="text-xs text-primary hover:underline flex items-center gap-1">
                      <Plus className="h-3 w-3" />
                      Neu
                    </Link>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {persons.map(person => (
                      <button
                        key={person.id}
                        onClick={() => togglePersonSelection(person.id)}
                        className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium transition-all border ${
                          selectedPersons.includes(person.id)
                            ? 'bg-primary/10 border-primary text-primary'
                            : 'bg-muted/50 border-transparent hover:border-muted-foreground/30'
                        }`}
                        disabled={isGenerating}
                        data-testid={`select-person-${person.id}`}
                      >
                        <div className="w-5 h-5 rounded-full bg-muted overflow-hidden">
                          <img
                            src={`${API}/persons/${person.id}/image?token=${localStorage.getItem('auth_token')}`}
                            alt={person.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                        <span className="truncate max-w-[100px]">{person.name}</span>
                        {selectedPersons.includes(person.id) && (
                          <CheckCircle2 className="h-3 w-3" />
                        )}
                      </button>
                    ))}
                  </div>
                  {selectedPersons.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {selectedPersons.length} Person(en) ausgewählt
                    </p>
                  )}
                </div>
              )}

              {/* Background Mode Toggle */}
              <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/10">
                <div>
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Activity className="h-4 w-4 text-primary" />
                    Hintergrund-Modus
                  </label>
                  <p className="text-xs text-muted-foreground">Du kannst die Seite verlassen</p>
                </div>
                <Switch
                  checked={useBackgroundMode}
                  onCheckedChange={setUseBackgroundMode}
                  disabled={isGenerating}
                  data-testid="background-mode-toggle"
                />
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !canGenerate}
                className="w-full"
                data-testid="generate-button"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    {useBackgroundMode ? 'Starte Auftrag...' : `Generiere ${variationsCount} Thumbnail${variationsCount > 1 ? 's' : ''}...`}
                  </>
                ) : (
                  <>
                    {useBackgroundMode ? (
                      <>
                        <Activity className="h-4 w-4 mr-2" />
                        Auftrag starten
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        {generatorMode === 'video-url' && useSmartRandom 
                          ? `🎲 ${variationsCount} Smart Thumbnail${variationsCount > 1 ? 's' : ''} generieren`
                          : `${variationsCount} Thumbnail${variationsCount > 1 ? 's' : ''} generieren`}
                      </>
                    )}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Recent Thumbnails */}
          {generatedThumbnails.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Letzte Thumbnails</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {generatedThumbnails.slice(0, 6).map(thumb => (
                    <div
                      key={thumb.id}
                      className={`relative aspect-video rounded-md overflow-hidden bg-muted cursor-pointer border-2 transition-all ${
                        selectedThumbnail?.id === thumb.id ? 'border-primary' : 'border-transparent hover:border-muted-foreground/30'
                      }`}
                      onClick={() => handleSelectThumbnail(thumb)}
                      data-testid={`thumbnail-preview-${thumb.id}`}
                    >
                      <img
                        src={`${API}/thumbnails/${thumb.id}/image?token=${encodeURIComponent(localStorage.getItem('auth_token') || '')}`}
                        alt={thumb.video_title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = ''; e.target.className = 'hidden'; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 hover:opacity-100 transition-opacity">
                        <p className="absolute bottom-1 left-1 right-1 text-xs text-white truncate">
                          {thumb.video_title}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Preview Panel */}
        <div>
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Vorschau
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isGenerating ? (
                <div className="aspect-video bg-muted rounded-lg flex flex-col items-center justify-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary mb-2" />
                  <p className="text-sm text-muted-foreground">Generiere Thumbnail...</p>
                  <p className="text-xs text-muted-foreground mt-1">Dies kann 10-30 Sekunden dauern</p>
                </div>
              ) : selectedThumbnail?.image_data ? (
                <div className="space-y-4">
                  {/* Variations Grid (when multiple) */}
                  {generatedVariations.length > 1 && (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground font-medium">
                        {generatedVariations.length} Varianten generiert - wähle deine Lieblingsvariante:
                      </p>
                      <div className={`grid gap-2 ${generatedVariations.length <= 2 ? 'grid-cols-2' : 'grid-cols-2'}`}>
                        {generatedVariations.map((variant, idx) => (
                          <div
                            key={variant.id}
                            onClick={() => setSelectedThumbnail(variant)}
                            className={`relative aspect-video rounded-md overflow-hidden cursor-pointer border-2 transition-all ${
                              selectedThumbnail?.id === variant.id 
                                ? 'border-primary ring-2 ring-primary/30' 
                                : 'border-muted hover:border-muted-foreground/50'
                            }`}
                          >
                            <img
                              src={`data:image/png;base64,${variant.image_data}`}
                              alt={`Variante ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                              #{idx + 1}
                            </div>
                            {selectedThumbnail?.id === variant.id && (
                              <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5">
                                <CheckCircle2 className="h-3 w-3" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Main Preview */}
                  <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                    <img
                      src={`data:image/png;base64,${selectedThumbnail.image_data}`}
                      alt={selectedThumbnail.video_title}
                      className="w-full h-full object-cover"
                      data-testid="generated-thumbnail-preview"
                    />
                  </div>
                  
                  {/* Thumbnail Info */}
                  <div className="space-y-2 text-sm">
                    <p className="font-medium">{selectedThumbnail.video_title}</p>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Badge variant="secondary">{selectedThumbnail.style_name}</Badge>
                      <span>{selectedThumbnail.generation_time_ms}ms</span>
                      {selectedThumbnail.variation_number && (
                        <Badge variant="outline">Variante {selectedThumbnail.variation_number}</Badge>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button onClick={handleDownload} className="flex-1" data-testid="download-button">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => navigate(`/editor?id=${selectedThumbnail.id}`)}
                      data-testid="edit-thumbnail-btn"
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Bearbeiten
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="icon" data-testid="delete-thumbnail-btn">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Thumbnail löschen?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Möchtest du dieses Thumbnail wirklich löschen?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteThumbnail(selectedThumbnail.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Löschen
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>

                  {/* Prompt Used (Collapsible) */}
                  <Collapsible>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="w-full justify-start">
                        <ChevronRight className="h-4 w-4 mr-1" />
                        Verwendeter Prompt anzeigen
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-3 bg-muted/50 rounded-md mt-2">
                        <pre className="text-xs whitespace-pre-wrap">{selectedThumbnail.prompt_used}</pre>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              ) : (
                <div className="aspect-video bg-muted/50 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-muted-foreground/20">
                  <Sparkles className="h-10 w-10 text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">Noch kein Thumbnail generiert</p>
                  <p className="text-xs text-muted-foreground mt-1">Wähle einen Style und gib einen Titel ein</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ============== Jobs Page ==============
const JobsPage = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [styles, setStyles] = useState([]);
  const [persons, setPersons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [newJobId, setNewJobId] = useState(null);
  const [showNewJobBanner, setShowNewJobBanner] = useState(false);
  const [recipeJob, setRecipeJob] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalJobs, setTotalJobs] = useState(0);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'thumbnail_generation', 'meta_ad_generation', 'meta_ad_template'
  
  // Caption generator state
  const [generatedCaptions, setGeneratedCaptions] = useState([]);
  const [generatingCaptions, setGeneratingCaptions] = useState(false);

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);

  // Story preview dialog state (for 9:16 Meta Ads)
  const [storyPreviewSrc, setStoryPreviewSrc] = useState(null);
  const [showCarouselSafeZones, setShowCarouselSafeZones] = useState(false);
  // Carousel preview dialog state
  const [carouselPreviewSlides, setCarouselPreviewSlides] = useState(null);

  // Save as Vorlage dialog state
  const [savePresetDialogOpen, setSavePresetDialogOpen] = useState(false);
  const [savePresetJobId, setSavePresetJobId] = useState(null);
  const [savePresetName, setSavePresetName] = useState("");

  const handleSaveJobAsPreset = async () => {
    if (!savePresetName.trim() || !savePresetJobId) return;
    try {
      // Find the job to determine type
      const job = jobs.find(j => j.id === savePresetJobId);
      const jobType = job?.job_type;
      
      if (jobType === 'thumbnail_generation') {
        // For thumbnails: save as thumbnail template using first result thumbnail
        const thumbId = job?.result_ids?.[0];
        if (!thumbId) { toast.error("Kein Thumbnail zum Speichern gefunden"); return; }
        await axios.post(`${API}/thumbnail-templates`, { thumbnail_id: thumbId, name: savePresetName });
      } else {
        // For meta ads: save as meta ad preset
        const jobRes = await axios.get(`${API}/jobs/${savePresetJobId}/wizard-data`);
        const d = jobRes.data?.input_data || {};
        await axios.post(`${API}/meta-ads/presets`, { name: savePresetName, ...d });
      }
      toast.success("Vorlage gespeichert!");
      setSavePresetDialogOpen(false);
      setSavePresetName("");
    } catch (err) {
      toast.error("Fehler beim Speichern der Vorlage");
    }
  };

  // Get URL parameters and create optimistic placeholder job
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const newId = urlParams.get('new');
    if (newId) {
      setNewJobId(newId);
      setShowNewJobBanner(true);
      
      // Create an optimistic placeholder job that shows IMMEDIATELY
      // This ensures the user sees something even if API is slow/blocked
      const placeholderJob = {
        id: newId,
        job_type: 'thumbnail_generation',
        status: 'pending',
        progress: 0,
        title: 'Thumbnail wird generiert...',
        description: 'Auftrag wird bearbeitet',
        created_at: new Date().toISOString(),
        input_data: { video_title: 'Neuer Thumbnail-Auftrag' },
        _isPlaceholder: true  // Mark as placeholder
      };
      
      // Add placeholder to jobs list immediately
      setJobs(prev => {
        // Don't add if job already exists
        if (prev.some(j => j.id === newId)) return prev;
        return [placeholderJob, ...prev];
      });
      
      // Robust: diesen Job DIREKT pollen bis er fertig/fehlgeschlagen ist
      // (überlebt Listen-Cache, Filter und stille Poll-Fehler)
      let directPollCount = 0;
      const directPoll = setInterval(async () => {
        directPollCount += 1;
        try {
          const res = await axios.get(`${API}/jobs/${newId}`);
          const fresh = res.data;
          if (fresh?.id) {
            setJobs(prev => prev.some(j => j.id === fresh.id)
              ? prev.map(j => (j.id === fresh.id ? fresh : j))
              : [fresh, ...prev]);
            if (fresh.status === 'completed' || fresh.status === 'failed') {
              clearInterval(directPoll);
              if (fresh.status === 'failed') {
                toast.error(fresh.error_message || 'Generierung fehlgeschlagen');
              }
            }
          }
        } catch (e) { /* nächster Versuch */ }
        if (directPollCount > 150) clearInterval(directPoll); // max ~10 Min
      }, 4000);
      
      // Clear URL parameter without reload
      window.history.replaceState({}, '', '/jobs');
      // Hide banner after 10 seconds
      setTimeout(() => setShowNewJobBanner(false), 10000);
      
      return () => clearInterval(directPoll);
    }
  }, []);

  // Auto-open the result sheet when navigating back from the editor via ?openJob=<id>.
  // This restores the previously-viewed ad-set instead of dropping the user into the bare library.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openJobId = params.get('openJob');
    if (!openJobId || jobs.length === 0) return;

    // Only auto-open once per mount + only if the job is actually in the loaded list.
    const targetJob = jobs.find(j => j.id === openJobId);
    if (!targetJob) return;

    // Clear the URL param so refreshing doesn't re-trigger.
    window.history.replaceState({}, '', '/jobs');
    handleViewResult(targetJob);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  // Fetch jobs with pagination
  const fetchJobs = useCallback(async (reset = false, filterType = null) => {
    try {
      const currentFilter = filterType !== null ? filterType : activeFilter;
      const skip = reset ? 0 : jobs.length;
      const params = new URLSearchParams({ skip: String(skip), limit: '20' });
      if (currentFilter !== 'all') params.set('job_type', currentFilter);
      
      const response = await axios.get(`${API}/jobs?${params}`);
      const data = response.data;
      const jobsData = data.jobs || [];
      
      if (reset) {
        // Keep placeholders
        const placeholders = jobs.filter(j => j._isPlaceholder && !jobsData.some(d => d.id === j.id));
        setJobs([...placeholders, ...jobsData]);
      } else {
        setJobs(prev => {
          const existingIds = new Set(prev.map(j => j.id));
          const newJobs = jobsData.filter(j => !existingIds.has(j.id));
          return [...prev, ...newJobs];
        });
      }
      
      setHasMore(data.has_more ?? jobsData.length >= 20);
      setTotalJobs(data.total || 0);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [activeFilter, jobs.length]);

  const loadMore = () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    fetchJobs(false);
  };

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 500) {
        loadMore();
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setJobs([]);
    setIsLoading(true);
    fetchJobs(true, filter);
  };

  // Fetch persons for name resolution
  useEffect(() => {
    const fetchPersonsData = async () => {
      try {
        const response = await axios.get(`${API}/persons`);
        // Only update if we got valid data - keep existing on empty/error
        if (response.data && (Array.isArray(response.data) ? response.data.length > 0 : true)) {
          setPersons(response.data);
        }
      } catch (err) {
        console.error('Error fetching persons:', err);
        // Keep existing data on error - don't clear
      }
    };
    fetchPersonsData();
  }, []);

  // Fetch styles for name resolution
  useEffect(() => {
    const fetchStylesData = async () => {
      try {
        const response = await axios.get(`${API}/styles`);
        const stylesData = response.data || [];
        setStyles(prev => {
          // Keep existing data if new data is empty
          if (stylesData.length === 0 && prev.length > 0) {
            console.log('Keeping existing styles - new data is empty');
            return prev;
          }
          return stylesData;
        });
      } catch (err) {
        console.error('Error fetching styles:', err);
        // Keep existing data on error - don't clear
      }
    };
    fetchStylesData();
  }, []);

  // Helper function to get person name from ID
  const getPersonName = (personIdOrObj) => {
    if (typeof personIdOrObj === 'object' && personIdOrObj?.name) {
      return personIdOrObj.name;
    }
    const person = persons.find(p => p.id === personIdOrObj);
    return person?.name || 'Unbekannte Person';
  };

  // Helper function to get style name from ID
  const getStyleName = (styleIdOrName) => {
    if (!styleIdOrName) return null;
    // If it's already a name (not UUID format), return it
    if (styleIdOrName && !styleIdOrName.match(/^[0-9a-f]{8}-[0-9a-f]{4}-/i)) {
      return styleIdOrName;
    }
    const style = styles.find(s => s.id === styleIdOrName);
    return style?.name || styleIdOrName;
  };

  // Helper function to get tone case label
  const getToneCaseLabel = (toneCase) => {
    const toneCases = {
      'business': { label: '💼 Business', emoji: '💼' },
      'professional_appealing': { label: '✨ Professionell ansprechend', emoji: '✨' },
      'professional_viral': { label: '🚀 Professionell viral', emoji: '🚀' },
      'youtube_viral': { label: '🔥 YouTube viral', emoji: '🔥' },
      'ai_decides': { label: '🤖 KI entscheidet', emoji: '🤖' },
      'uppercase': { label: '🔠 GROSSBUCHSTABEN', emoji: '🔠' },
      'lowercase': { label: '🔡 kleinbuchstaben', emoji: '🔡' },
      'normal': { label: '📝 Normal', emoji: '📝' }
    };
    return toneCases[toneCase] || { label: toneCase, emoji: '📝' };
  };

  useEffect(() => {
    fetchJobs(true);
    
    // Polling: refresh only active jobs status (not full reload)
    let pollFailures = 0;
    const pollInterval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/jobs?skip=0&limit=20${activeFilter !== 'all' ? `&job_type=${activeFilter}` : ''}`);
        pollFailures = 0;
        const freshJobs = res.data.jobs || [];
        setJobs(prev => {
          // Update existing jobs with fresh data, keep the rest, prepend genuinely new jobs
          const freshMap = new Map(freshJobs.map(j => [j.id, j]));
          const updated = prev.map(j => freshMap.get(j.id) || j);
          const known = new Set(updated.map(j => j.id));
          const newcomers = freshJobs.filter(j => !known.has(j.id));
          return newcomers.length ? [...newcomers, ...updated] : updated;
        });
        setTotalJobs(res.data.total || 0);
      } catch (e) {
        pollFailures += 1;
        if (pollFailures === 3) {
          console.error('Job-Polling 3x fehlgeschlagen:', e);
          fetchJobs(true);
        }
      }
    }, 5000);
    
    return () => clearInterval(pollInterval);
  }, [activeFilter]);

  const handleDeleteJob = async (jobId) => {
    try {
      await axios.delete(`${API}/jobs/${jobId}`);
      setJobs(prev => prev.filter(j => j.id !== jobId));
      if (selectedJob?.id === jobId) setSelectedJob(null);
      toast.success('Auftrag und Ergebnisse gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleViewResult = async (job) => {
    if (job.status !== 'completed') return;
    
    try {
      // Handle Meta Ad Template jobs - use result_ids or fallback to inline images
      if (job.job_type === 'meta_ad_template') {
        // New approach: use result_ids
        if (job.result_ids?.length > 0) {
          const thumbnailPromises = job.result_ids.map(id => 
            axios.get(`${API}/thumbnails/${id}`).then(res => res.data).catch(() => null)
          );
          const thumbnails = (await Promise.all(thumbnailPromises)).filter(t => t !== null);
          
          if (thumbnails.length > 0) {
            const allImages = thumbnails.map(t => ({
              id: t.id,
              image_data: t.image_data,
              video_title: `${job.title} (${t.meta_ad_format || t.export_format || t.format || ''})`,
              format: t.meta_ad_format || t.format,
              dimensions: t.meta_ad_dimensions ? `${t.meta_ad_dimensions[0]}x${t.meta_ad_dimensions[1]}` : '',
              variation: t.variation_number || t.variation,
              ai_background: t.ai_background
            }));
            
            setSelectedJob({ 
              ...job, 
              thumbnails: allImages,
              selectedThumbnailIndex: 0,
              isMetaAd: true,
              isTemplateAd: true
            });
            return;
          }
        }
        
        // Fallback for older jobs with inline images
        try {
          const fullJobRes = await axios.get(`${API}/jobs/${job.id}`);
          const fullJob = fullJobRes.data;
          const images = fullJob.result_data?.images || [];
          
          const allImages = images.map((img, idx) => ({
            id: img.id || `template-${idx}`,
            image_data: img.image || img.image_data,
            video_title: `${fullJob.result_data?.template_name || 'Template'} (${img.export_format || img.format})`,
            format: img.format,
            dimensions: `${img.width}x${img.height}`,
            variation: img.variation,
            ai_background: img.ai_background
          }));
          
          if (allImages.length > 0) {
            setSelectedJob({ 
              ...fullJob, 
              thumbnails: allImages,
              selectedThumbnailIndex: 0,
              isMetaAd: true,
              isTemplateAd: true
            });
          } else {
            toast.error('Keine Bilder in diesem Auftrag gefunden');
          }
        } catch (err) {
          console.error('Error loading template ad job:', err);
          toast.error('Fehler beim Laden der Template-Bilder');
        }
        return;
      }
      
      // Handle Meta Ads + Carousel - use result_ids to fetch images individually (avoids 16MB MongoDB limit)
      if (job.job_type === 'meta_ad_generation' || job.job_type === 'meta_carousel_generation') {
        // Check if job actually failed (no images generated)
        if (job.status === 'failed') {
          toast.error(job.error_message || 'Bildgenerierung fehlgeschlagen. Bitte versuche es erneut.');
          return;
        }
        
        if ((!job.result_ids || job.result_ids.length === 0)) {
          // No result_ids at all — check if result_data has inline images (old format)
          try {
            const fullJobRes = await axios.get(`${API}/jobs/${job.id}`);
            const fullJob = fullJobRes.data;
            
            if (fullJob.status === 'failed') {
              toast.error(fullJob.error_message || 'Bildgenerierung fehlgeschlagen. Bitte versuche es erneut.');
              return;
            }
            
            const formats = fullJob.result_data?.formats || {};
            const directImages = fullJob.result_data?.images || [];
            const allImages = [];
            
            if (directImages.length > 0) {
              directImages.forEach((imgData, idx) => {
                allImages.push({
                  id: `meta-ad-${idx}`,
                  image_data: imgData,
                  video_title: `${fullJob.title || 'Meta Ad'} - Bild ${idx + 1}`,
                  format: 'square',
                  dimensions: '1080x1080',
                  variation: idx + 1
                });
              });
            }
            
            Object.entries(formats).forEach(([formatType, images]) => {
              images.forEach((img, idx) => {
                allImages.push({
                  id: img.id || `${formatType}-${idx}`,
                  image_data: img.image_data,
                  video_title: `${job.title} (${formatType})`,
                  format: formatType,
                  dimensions: img.dimensions,
                  variation: img.variation
                });
              });
            });
            
            if (allImages.length > 0) {
              setSelectedJob({ 
                ...fullJob, 
                thumbnails: allImages,
                selectedThumbnailIndex: 0,
                isMetaAd: fullJob.job_type !== 'meta_carousel_generation',
                isCarousel: fullJob.job_type === 'meta_carousel_generation',
              });
            } else {
              toast.error('Bildgenerierung fehlgeschlagen — es wurden keine Bilder erzeugt. Bitte versuche es erneut.');
            }
          } catch (err) {
            console.error('Error loading meta ad job:', err);
            toast.error('Fehler beim Laden der Meta Ad Bilder');
          }
          return;
        }
        
        // New approach: fetch ONLY metadata (fast!) and use URL-based image loading
        try {
          const metaRes = await axios.post(`${API}/thumbnails/batch-meta`, { ids: job.result_ids });
          const metas = metaRes.data || [];
          
          if (metas.length > 0) {
            const allImages = metas.map(t => ({
              id: t.id,
              imageUrl: `${API}/thumbnails/${t.id}/image?token=${encodeURIComponent(localStorage.getItem('auth_token') || '')}`,
              video_title: `${job.title} (${t.meta_ad_format || t.aspect_ratio || t.format || ''})`,
              // For carousel slides the real format is the aspect_ratio (4:5 / 1:1);
              // for classic meta ads it's meta_ad_format (square / 9:16 / 1200x628 …).
              format: t.meta_ad_format || (t.format === 'carousel' ? t.aspect_ratio : t.format) || 'square',
              dimensions: t.meta_ad_dimensions || t.dimensions,
              variation: t.variation_number || t.variation,
              // Carousel-specific metadata for display + filename
              carousel_slide_number: t.carousel_slide_number,
              carousel_total_slides: t.carousel_total_slides,
              carousel_slide_type: t.carousel_slide_type,
              carousel_headline: t.carousel_headline,
              is_carousel_slide: t.format === 'carousel' || !!t.carousel_slide_number,
            }));
            
            setSelectedJob({ 
              ...job, 
              thumbnails: allImages,
              selectedThumbnailIndex: 0,
              isMetaAd: job.job_type !== 'meta_carousel_generation',
              isCarousel: job.job_type === 'meta_carousel_generation',
            });
          } else {
            toast.error('Bildgenerierung fehlgeschlagen — es wurden keine Bilder erzeugt.');
          }
        } catch (err) {
          console.error(`[Job ${job.id}] Batch meta fetch failed:`, err);
          toast.error('Fehler beim Laden der Bilder-Metadaten');
        }
        return;
      }
      
      // YouTube thumbnails - use result_ids
      if (!job.result_ids?.length) return;
      
      // Fetch only metadata (fast!), use URL-based image loading
      try {
        const metaRes = await axios.post(`${API}/thumbnails/batch-meta`, { ids: job.result_ids });
        const metas = metaRes.data || [];
        const thumbnails = metas.map(t => ({
          ...t,
          imageUrl: `${API}/thumbnails/${t.id}/image?token=${encodeURIComponent(localStorage.getItem('auth_token') || '')}`
        }));
        
        setSelectedJob({ 
          ...job, 
          thumbnails: thumbnails,
          selectedThumbnailIndex: 0 
        });
      } catch {
        // Fallback: fetch full thumbnails individually
        const thumbnailPromises = job.result_ids.map(id => 
          axios.get(`${API}/thumbnails/${id}`).then(res => res.data).catch(() => null)
        );
        const thumbnails = (await Promise.all(thumbnailPromises)).filter(t => t !== null);
        
        setSelectedJob({ 
          ...job, 
          thumbnails: thumbnails,
          selectedThumbnailIndex: 0 
        });
      }
    } catch (err) {
      console.error('Error loading results:', err);
      toast.error('Fehler beim Laden der Ergebnisse');
    }
  };

  const handleDownload = async (thumbnail) => {
    // For carousel slides → friendly filename "Karussell-Slide-3-list.png"
    let filename;
    if (thumbnail.is_carousel_slide || thumbnail.carousel_slide_number) {
      const n = thumbnail.carousel_slide_number || 1;
      const total = thumbnail.carousel_total_slides || '';
      const type = thumbnail.carousel_slide_type || '';
      filename = `Karussell-Slide-${n}${total ? `-von-${total}` : ''}${type ? `-${type}` : ''}.png`;
    } else {
      const formatLabel = thumbnail.format === "9:16" || thumbnail.format === "1080x1920" ? "Story" 
        : thumbnail.format === "1:1" || thumbnail.format === "1080x1080" ? "Square"
        : thumbnail.format || "Bild";
      const variation = thumbnail.variation || 1;
      filename = `${formatLabel}_${variation}.png`;
    }
    
    // If we have a URL, fetch the binary image directly
    if (thumbnail.imageUrl) {
      try {
        const response = await axios.get(thumbnail.imageUrl, { responseType: 'blob' });
        const url = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('Download gestartet!');
      } catch (err) {
        console.error('Download failed:', err);
        toast.error('Download fehlgeschlagen');
      }
      return;
    }
    
    // Fallback: base64 data
    if (!thumbnail?.image_data) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${thumbnail.image_data}`;
    link.download = filename;
    link.click();
    toast.success('Download gestartet!');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Loader2 className="h-4 w-4 text-muted-foreground animate-pulse" />;
      case 'processing': 
      case 'analyzing': 
      case 'running': 
      case 'generating':
        return <Loader2 className="h-4 w-4 text-primary animate-spin" />;
      case 'completed': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed': return <AlertCircle className="h-4 w-4 text-destructive" />;
      default: return <Loader2 className="h-4 w-4 text-muted-foreground animate-pulse" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Wird gestartet...';
      case 'processing': 
      case 'analyzing': 
      case 'running': 
      case 'generating':
        return 'In Bearbeitung';
      case 'completed': return 'Fertig';
      case 'failed': return 'Fehlgeschlagen';
      default: return 'Wird gestartet...';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-500/15 text-amber-600 dark:text-amber-400';
      case 'processing': 
      case 'analyzing': 
      case 'running': 
      case 'generating':
        return 'bg-primary/15 text-primary';
      case 'completed': return 'bg-green-500/15 text-green-600 dark:text-green-400';
      case 'failed': return 'bg-destructive/15 text-destructive';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const isActiveStatus = (status) => ['pending', 'processing', 'analyzing', 'running', 'generating'].includes(status);

  const activeJobs = jobs.filter(j => isActiveStatus(j.status));
  const completedJobs = jobs.filter(j => j.status === 'completed');
  const failedJobs = jobs.filter(j => j.status === 'failed');

  // Check if the new job exists in the list
  const newJobExists = newJobId && jobs.some(j => j.id === newJobId);
  const newJob = jobs.find(j => j.id === newJobId);

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="jobs-page">
      {/* New Job Banner */}
      {showNewJobBanner && (
        <div className="mb-4 p-4 rounded-lg bg-primary/10 border border-primary/30 flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-primary animate-spin" />
          <div className="flex-1">
            <p className="font-medium text-primary">Neuer Auftrag gestartet!</p>
            <p className="text-sm text-muted-foreground">
              {newJobExists 
                ? `"${newJob?.input_data?.video_title || 'Thumbnail'}" wird generiert...`
                : 'Auftrag wird erstellt...'
              }
            </p>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowNewJobBanner(false)}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Activity className="h-6 w-6 text-primary" />
          Meine Aufträge
        </h1>
        <p className="text-muted-foreground mt-1">
          Alle Hintergrund-Aufträge an einem Ort. Du kannst die Seite verlassen und später zurückkehren.
        </p>
      </div>

      {/* Stats + Filter Tabs */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-muted-foreground">{totalJobs} Aufträge</span>
          {activeJobs.length > 0 && (
            <span className="flex items-center gap-1 text-primary">
              <Loader2 className="h-3 w-3 animate-spin" />
              {activeJobs.length} aktiv
            </span>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[
          { id: 'all', label: 'Alle' },
          { id: 'thumbnail_generation', label: 'Thumbnails', icon: Youtube },
          { id: 'meta_ad_generation', label: 'Meta Ads', icon: Instagram },
          { id: 'meta_carousel_generation', label: 'Karussell', icon: Layers },
          { id: 'meta_ad_template', label: 'Templates', icon: LayoutTemplate },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => handleFilterChange(tab.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
              activeFilter === tab.id ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80 text-muted-foreground'
            }`}
          >
            {tab.icon && <tab.icon className="h-3.5 w-3.5" />}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Jobs List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-1/3 mb-2" />
                    <Skeleton className="h-3 w-1/4" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : jobs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {jobs.map(job => (
            <Card 
              key={job.id} 
              className={`transition-all hover:shadow-md overflow-hidden ${job.status === 'completed' ? 'cursor-pointer' : ''}`}
              onClick={() => job.status === 'completed' && handleViewResult(job)}
              data-testid={`job-card-${job.id}`}
            >
              <CardContent className="p-0">
                <div className="flex">
                  {/* Preview Image / Status */}
                  <div className="w-24 h-24 flex-shrink-0 bg-muted flex items-center justify-center overflow-hidden">
                    {job.status === 'completed' ? (
                      <img
                        src={job.result_ids?.length > 0
                          ? `${API}/thumbnails/${job.result_ids[0]}/image?token=${encodeURIComponent(localStorage.getItem('auth_token') || '')}`
                          : `${API}/jobs/${job.id}/preview?token=${localStorage.getItem('auth_token')}`
                        }
                        alt=""
                        className="h-full w-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full w-full text-green-500"><svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>'; }}
                      />
                    ) : (
                      <div className={`h-full w-full flex items-center justify-center ${getStatusColor(job.status)}`}>
                        {getStatusIcon(job.status)}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-3 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          {job.job_type === 'meta_ad_template' ? (
                            <LayoutTemplate className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                          ) : job.job_type === 'meta_ad_generation' ? (
                            <Instagram className="h-3.5 w-3.5 text-pink-500 flex-shrink-0" />
                          ) : (
                            <Youtube className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                          )}
                          <h3 className="font-medium text-sm truncate">{job.title || 'Unbenannter Auftrag'}</h3>
                          <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${getStatusColor(job.status)}`}>
                            {getStatusText(job.status)}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{job.description}</p>
                      </div>
                      
                      {/* Actions compact */}
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        {job.status === 'completed' && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); handleViewResult(job); }} title="Ergebnis ansehen">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {job.status === 'completed' && (job.job_type === 'meta_ad_generation' || job.job_type === 'meta_ad_template') && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-primary" title="Nochmal verwenden"
                            data-testid={`reuse-job-${job.id}`}
                            onClick={(e) => { e.stopPropagation(); navigate(`/meta-generator?reuse=${job.id}`); }}>
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {job.status === 'completed' && job.job_type === 'meta_carousel_generation' && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-primary" title="Nochmal verwenden"
                            data-testid={`reuse-job-${job.id}`}
                            onClick={(e) => { e.stopPropagation(); navigate(`/carousel?reuse=${job.id}`); }}>
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {job.status === 'completed' && job.job_type === 'thumbnail_generation' && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-primary" title="Nochmal verwenden"
                            data-testid={`reuse-job-${job.id}`}
                            onClick={(e) => { e.stopPropagation(); navigate(`/generator?reuse=${job.id}`); }}>
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {job.status === 'completed' && (job.job_type === 'meta_ad_generation' || job.job_type === 'meta_ad_template' || job.job_type === 'thumbnail_generation') && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-amber-500" title="Als Vorlage speichern"
                            data-testid={`save-preset-job-${job.id}`}
                            onClick={(e) => { e.stopPropagation(); setSavePresetJobId(job.id); setSavePresetName(job.title?.replace(/^(Meta Ad|Thumbnail):\s*/i, '').slice(0, 50) || ''); setSavePresetDialogOpen(true); }}>
                            <Save className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground" onClick={(e) => { e.stopPropagation(); setShareTarget(job); setShareDialogOpen(true); }}>
                          <Share2 className="h-3.5 w-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={(e) => e.stopPropagation()}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Auftrag löschen?</AlertDialogTitle>
                              <AlertDialogDescription>Der Auftrag und alle Ergebnisse werden gelöscht.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteJob(job.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Löschen</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>

                    {/* Progress bar */}
                    {isActiveStatus(job.status) && (
                      <div className="mt-1.5">
                        <Progress value={job.progress || 0} className="h-1" />
                        <p className="text-[10px] text-muted-foreground mt-0.5">{job.progress ? `${job.progress}%` : 'Wird gestartet...'}</p>
                      </div>
                    )}

                    {/* Error */}
                    {job.status === 'failed' && job.error_message && (
                      <p className="text-[10px] text-destructive mt-1 truncate">{job.error_message}</p>
                    )}

                    {/* Recipe badges - compact */}
                    {job.status === 'completed' && job.input_data && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        {job.input_data.ad_type && (
                          <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-pink-500/10 border-pink-500/30">
                            {job.input_data.ad_type === 'recruiting' ? 'Recruiting' : 'Service'}
                          </Badge>
                        )}
                        {job.input_data.target_audience && (
                          <Badge variant="outline" className="text-[10px] h-5 px-1.5">{job.input_data.target_audience.toUpperCase()}</Badge>
                        )}
                        {(job.input_data.style_name || job.input_data.style_id) && (
                          <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-purple-500/10 border-purple-500/30">
                            {getStyleName(job.input_data.style_name || job.input_data.style_id)}
                          </Badge>
                        )}
                        {job.input_data.brand_colors?.length > 0 && (
                          <Badge variant="outline" className="text-[10px] h-5 px-1.5 gap-0.5">
                            {job.input_data.brand_colors.slice(0, 3).map((c, i) => (
                              <span key={i} className="w-2.5 h-2.5 rounded-full border" style={{ backgroundColor: c }} />
                            ))}
                          </Badge>
                        )}
                        {job.input_data.tone_case && (
                          <Badge variant="outline" className="text-[10px] h-5 px-1.5">{getToneCaseLabel(job.input_data.tone_case).label}</Badge>
                        )}
                        {job.input_data.variations && (
                          <Badge variant="outline" className="text-[10px] h-5 px-1.5">{job.input_data.variations}x</Badge>
                        )}
                        <button
                          className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                          onClick={(e) => { e.stopPropagation(); setRecipeJob(job); }}
                        >
                          <Settings2 className="h-3 w-3" />
                          Details
                        </button>
                        <span className="text-[10px] text-muted-foreground ml-auto">
                          {new Date(job.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Activity className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
              <h3 className="font-medium mb-1">Noch keine Aufträge</h3>
              <p className="text-sm text-muted-foreground">
                Starte eine Thumbnail-Generierung im Hintergrund
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Result Sheet */}
      <Sheet open={!!selectedJob?.thumbnails} onOpenChange={() => setSelectedJob(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Ergebnis: {selectedJob?.title}</SheetTitle>
            <SheetDescription>
              {selectedJob?.thumbnails?.length || 0} Variante(n) generiert
            </SheetDescription>
          </SheetHeader>
          
          {selectedJob?.thumbnails && selectedJob.thumbnails.length > 0 && (
            <div className="space-y-4 mt-6">
              {/* Safe-Zone Toggle (Carousel only) */}
              {selectedJob.isCarousel && (
                <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-medium">Instagram Safe-Zones einblenden</span>
                    <span className="text-[11px] text-muted-foreground truncate hidden sm:inline">
                      Roter Bereich = wird von der App überdeckt
                    </span>
                  </div>
                  <Button
                    variant={showCarouselSafeZones ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowCarouselSafeZones((v) => !v)}
                    data-testid="toggle-carousel-safe-zones"
                  >
                    {showCarouselSafeZones ? "An" : "Aus"}
                  </Button>
                </div>
              )}
              {/* Thumbnails Grid */}
              <div className={`grid gap-3 ${selectedJob.thumbnails.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {selectedJob.thumbnails.map((thumb, index) => (
                  <div 
                    key={thumb.id || index}
                    className={`group relative rounded-lg overflow-hidden border bg-muted ${
                      thumb.format === '9:16' ? 'aspect-[9/16] max-h-[400px]' : 
                      thumb.format === '1200x628' ? 'aspect-[1200/628]' : 
                      thumb.format === '4:5' || thumb.is_carousel_slide ? 'aspect-[4/5] max-h-[520px]' :
                      thumb.format === '1:1' ? 'aspect-square max-h-[450px]' :
                      'aspect-video'
                    }`}
                  >
                    <img
                      src={thumb.imageUrl || `data:image/png;base64,${thumb.image_data}`}
                      alt={`Variante ${index + 1}`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                    {/* Safe-Zone Overlay (Carousel slides only) */}
                    {thumb.is_carousel_slide && showCarouselSafeZones && (
                      <>
                        <div
                          className="absolute top-0 left-0 right-0 pointer-events-none bg-red-500/30 border-b border-red-500/60"
                          style={{ height: thumb.format === '1:1' ? '16%' : '15%' }}
                          data-testid={`safe-zone-top-${index}`}
                        >
                          <span className="absolute top-1 left-2 text-[10px] font-medium text-white drop-shadow uppercase tracking-wider">
                            Instagram UI · Username
                          </span>
                        </div>
                        <div
                          className="absolute bottom-0 left-0 right-0 pointer-events-none bg-red-500/30 border-t border-red-500/60"
                          style={{ height: thumb.format === '1:1' ? '28%' : '25%' }}
                          data-testid={`safe-zone-bottom-${index}`}
                        >
                          <span className="absolute bottom-1 left-2 text-[10px] font-medium text-white drop-shadow uppercase tracking-wider">
                            Instagram UI · CTA / Reaktionen / Dots
                          </span>
                        </div>
                      </>
                    )}
                    {/* Overlay with download and edit buttons */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => handleDownload(thumb)}
                        data-testid={`download-thumb-${index}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                      {selectedJob.isMetaAd && thumb.format === '9:16' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-white/90 hover:bg-white text-black border-white/40"
                          onClick={() => setStoryPreviewSrc(thumb.imageUrl || `data:image/png;base64,${thumb.image_data}`)}
                          data-testid={`preview-story-${index}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Vorschau
                        </Button>
                      )}
                      {(selectedJob.isMetaAd || selectedJob.isCarousel || thumb.is_carousel_slide) ? (
                        <Button 
                          size="sm" 
                          variant="secondary"
                          onClick={() => navigate(`/meta-ad-editor?jobId=${selectedJob.id}&index=${index}&fromJob=${selectedJob.id}`)}
                          data-testid={`edit-meta-ad-${index}`}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Bearbeiten
                        </Button>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="secondary"
                          onClick={() => navigate(`/editor?id=${thumb.id}&fromJob=${selectedJob.id}`)}
                          data-testid={`edit-thumb-${index}`}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Bearbeiten
                        </Button>
                      )}
                    </div>
                    {/* Format/Variation badge */}
                    <div className="absolute top-2 left-2 flex gap-1">
                      {thumb.is_carousel_slide && thumb.carousel_slide_number ? (
                        <Badge className="bg-primary/90 text-primary-foreground" data-testid={`carousel-slide-badge-${thumb.carousel_slide_number}`}>
                          Slide {thumb.carousel_slide_number}{thumb.carousel_total_slides ? `/${thumb.carousel_total_slides}` : ''}
                          {thumb.carousel_slide_type && thumb.carousel_slide_type !== 'story' ? ` · ${thumb.carousel_slide_type}` : ''}
                        </Badge>
                      ) : thumb.format ? (
                        <Badge className="bg-pink-500/90 text-white">
                          {thumb.format}
                        </Badge>
                      ) : null}
                      {selectedJob.thumbnails.length > 1 && !thumb.format && !thumb.is_carousel_slide && (
                        <Badge className="bg-black/70 text-white">
                          #{index + 1}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Info */}
              <div className="space-y-2 pt-2 border-t">
                <h4 className="font-medium">{selectedJob.thumbnails[0]?.video_title}</h4>
                <p className="text-sm text-muted-foreground">{selectedJob.thumbnails[0]?.style_name}</p>
              </div>

              {/* Download All as ZIP */}
              {selectedJob.thumbnails.length > 1 && (
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={async () => {
                    try {
                      toast.info('ZIP wird erstellt...');
                      const response = await axios.get(`${API}/jobs/${selectedJob.id}/download-all`, {
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `${selectedJob.title || selectedJob.job_type || 'export'}.zip`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('ZIP Download gestartet!');
                    } catch (err) {
                      console.error('ZIP download error:', err);
                      toast.error('ZIP Download fehlgeschlagen');
                    }
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Alle als ZIP herunterladen
                </Button>
              )}

              {/* Carousel Slideshow Preview */}
              {selectedJob.job_type === 'meta_carousel_generation' && selectedJob.thumbnails.length > 1 && (
                <Button
                  variant="default"
                  className="w-full"
                  onClick={() => {
                    // Sort slides by carousel_slide_number, build preview list
                    const sorted = [...selectedJob.thumbnails].sort(
                      (a, b) => (a.carousel_slide_number || 0) - (b.carousel_slide_number || 0)
                    );
                    setCarouselPreviewSlides(
                      sorted.map((t) => ({
                        imageSrc: t.imageUrl || `data:image/png;base64,${t.image_data}`,
                        headline: t.carousel_headline,
                        slide_number: t.carousel_slide_number,
                      }))
                    );
                  }}
                  data-testid="preview-carousel-slideshow"
                >
                  <Layers className="h-4 w-4 mr-2" />
                  Karussell als Slideshow ansehen
                </Button>
              )}

              {/* Recreate with same settings */}
              {selectedJob.input_data && (selectedJob.job_type === 'meta_ad_generation' || selectedJob.isMetaAd) && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    navigate(`/meta-generator?reuse=${selectedJob.id}`);
                    toast.success('Einstellungen übernommen — passe an und generiere neu!');
                  }}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Mit gleichen Einstellungen neu erstellen
                </Button>
              )}

              {selectedJob.input_data && (selectedJob.job_type === 'meta_carousel_generation' || selectedJob.isCarousel) && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  data-testid="reuse-carousel-from-sheet"
                  onClick={() => {
                    navigate(`/carousel?reuse=${selectedJob.id}`);
                    toast.success('Karussell-Einstellungen übernommen — passe an und generiere neu!');
                  }}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Karussell mit gleichen Einstellungen neu erstellen
                </Button>
              )}

              {/* Caption Generator for Meta Ads */}
              {selectedJob.input_data && (selectedJob.job_type === 'meta_ad_generation' || selectedJob.isMetaAd) && (
                <div className="pt-4 border-t space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-primary" />
                      Ad-Caption Vorschläge
                    </h4>
                    <Button
                      size="sm"
                      variant={generatedCaptions.length > 0 ? "outline" : "default"}
                      onClick={async () => {
                        setGeneratingCaptions(true);
                        try {
                          const inp = selectedJob.input_data;
                          const res = await axios.post(`${API}/meta-ads/generate-captions`, {
                            target_audience: inp.target_audience || '',
                            ad_type: inp.ad_type || '',
                            service_description: inp.service_description || '',
                            target_description: inp.target_description || '',
                            painpoints: inp.painpoints || '',
                            solution_promise: inp.solution_promise || '',
                            hook: inp.hook || '',
                            tone: inp.tone || 'professional',
                          });
                          setGeneratedCaptions(res.data.captions || []);
                          toast.success(`${res.data.captions?.length || 0} Caption-Varianten generiert!`);
                        } catch (err) {
                          toast.error('Fehler beim Generieren der Captions');
                        } finally {
                          setGeneratingCaptions(false);
                        }
                      }}
                      disabled={generatingCaptions}
                      data-testid="generate-captions-btn"
                    >
                      {generatingCaptions ? (
                        <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Generiere...</>
                      ) : generatedCaptions.length > 0 ? (
                        <><RefreshCw className="h-3.5 w-3.5 mr-1" /> Neu generieren</>
                      ) : (
                        <><Sparkles className="h-3.5 w-3.5 mr-1" /> Captions erstellen</>
                      )}
                    </Button>
                  </div>
                  {generatedCaptions.length > 0 && (
                    <div className="space-y-2">
                      {generatedCaptions.map((cap, i) => (
                        <div key={i} className="p-3 rounded-lg border bg-muted/30 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <Badge variant="secondary" className="text-xs">{cap.label || cap.type}</Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => { navigator.clipboard.writeText(cap.text); toast.success('Caption kopiert!'); }}
                              data-testid={`copy-caption-${i}`}
                            >
                              <Copy className="h-3 w-3 mr-1" /> Kopieren
                            </Button>
                          </div>
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{cap.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Version Timeline */}
              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  Version Timeline
                </h4>
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-border" />
                  
                  {/* Timeline items */}
                  <div className="space-y-3">
                    {/* Current version */}
                    <div className="relative flex gap-3 items-start">
                      <div className="w-4 h-4 rounded-full bg-primary border-2 border-background z-10 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="default" className="text-xs">Aktuell</Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(selectedJob.created_at).toLocaleString('de-DE')}
                          </span>
                        </div>
                        <p className="text-sm mt-1">{selectedJob.title}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedJob.input_data?.style_name && (
                            <Badge variant="outline" className="text-xs">{selectedJob.input_data.style_name}</Badge>
                          )}
                          {selectedJob.input_data?.variations && (
                            <Badge variant="outline" className="text-xs">{selectedJob.input_data.variations}x</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Previous versions hint */}
                    <div className="relative flex gap-3 items-start opacity-50">
                      <div className="w-4 h-4 rounded-full bg-muted border-2 border-background z-10 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">
                          Klicke auf "Neu erstellen" um eine neue Version zu generieren
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Load More / Infinite Scroll Indicator */}
      {!isLoading && hasMore && jobs.length > 0 && (
        <div className="flex justify-center py-6">
          {isLoadingMore ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Weitere Aufträge laden...</span>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={loadMore}>
              Mehr laden ({jobs.length} von {totalJobs})
            </Button>
          )}
        </div>
      )}

      {!isLoading && !hasMore && jobs.length > 0 && (
        <p className="text-center text-xs text-muted-foreground py-4">
          Alle {jobs.length} Aufträge geladen
        </p>
      )}

      {/* Recipe Drawer - shows full generation settings */}
      <Drawer open={!!recipeJob} onOpenChange={(open) => !open && setRecipeJob(null)}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Generierungs-Einstellungen
            </DrawerTitle>
            <DrawerDescription>
              {recipeJob?.title || "Thumbnail-Konfiguration"}
            </DrawerDescription>
          </DrawerHeader>
          
          {recipeJob?.input_data && (
            <div className="px-4 pb-4 space-y-4 overflow-auto">
              {/* Meta Ad: Ad Type */}
              {recipeJob.input_data.ad_type && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">📋 Ad-Typ</h4>
                  <Badge variant="secondary" className="bg-pink-500/10">
                    {recipeJob.input_data.ad_type === 'recruiting' ? '👥 Recruiting' : '🏢 Dienstleistung'}
                  </Badge>
                </div>
              )}

              {/* Meta Ad: Target Audience */}
              {recipeJob.input_data.target_audience && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">🎯 Zielgruppe</h4>
                  <Badge variant="secondary" className="bg-indigo-500/10">
                    {recipeJob.input_data.target_audience === 'b2b' ? 'B2B — Unternehmen & Geschäftskunden' : 'B2C — Endverbraucher & Privatpersonen'}
                  </Badge>
                </div>
              )}

              {/* Meta Ad: Hook */}
              {recipeJob.input_data.hook && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">💬 Hook</h4>
                  <p className="text-sm font-semibold bg-muted p-3 rounded-lg">{recipeJob.input_data.hook}</p>
                </div>
              )}

              {/* Meta Ad: Service Description */}
              {recipeJob.input_data.service_description && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">📝 Beschreibung</h4>
                  <p className="text-sm bg-muted p-3 rounded-lg">{recipeJob.input_data.service_description}</p>
                </div>
              )}

              {/* Meta Ad: Painpoints */}
              {recipeJob.input_data.painpoints && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">⚡ Painpoints</h4>
                  <p className="text-sm bg-muted p-3 rounded-lg">{recipeJob.input_data.painpoints}</p>
                </div>
              )}

              {/* Meta Ad: Solution */}
              {recipeJob.input_data.solution_promise && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">✅ Lösung</h4>
                  <p className="text-sm bg-muted p-3 rounded-lg">{recipeJob.input_data.solution_promise}</p>
                </div>
              )}

              {/* Meta Ad: CTA */}
              {recipeJob.input_data.cta && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">🔘 Call-to-Action</h4>
                  <Badge variant="secondary">{recipeJob.input_data.cta}</Badge>
                </div>
              )}

              {/* Meta Ad: Formats */}
              {recipeJob.input_data.formats?.length > 0 && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">📐 Formate</h4>
                  <div className="flex flex-wrap gap-2">
                    {recipeJob.input_data.formats.map((fmt, idx) => (
                      <Badge key={idx} variant="outline">{fmt}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Video Description */}
              {recipeJob.input_data.video_description && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">📝 Video-Beschreibung</h4>
                  <p className="text-sm bg-muted p-3 rounded-lg">{recipeJob.input_data.video_description}</p>
                </div>
              )}

              {/* Title */}
              {recipeJob.input_data.video_title && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">🏷️ Gewählter Titel</h4>
                  <p className="text-lg font-bold">{recipeJob.input_data.video_title}</p>
                </div>
              )}

              {/* Style */}
              {(recipeJob.input_data.style_name || recipeJob.input_data.style_id) && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">🎨 Style</h4>
                  <Badge variant="secondary" className="bg-purple-500/10">
                    <Palette className="h-4 w-4 mr-2" />
                    {getStyleName(recipeJob.input_data.style_name || recipeJob.input_data.style_id)}
                  </Badge>
                </div>
              )}

              {/* Brand Colors */}
              {recipeJob.input_data.brand_colors?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">🎨 Markenfarben</h4>
                  <div className="flex flex-wrap gap-2">
                    {recipeJob.input_data.brand_colors.map((color, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-lg">
                        <span 
                          className="w-6 h-6 rounded-full border shadow-sm"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-sm font-mono">{color}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tone Case - full label with description */}
              {recipeJob.input_data.tone_case && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">🎯 Stil/Ton</h4>
                  <Badge variant="secondary" className="bg-blue-500/10 text-blue-700 dark:text-blue-300">
                    {getToneCaseLabel(recipeJob.input_data.tone_case).label}
                  </Badge>
                </div>
              )}

              {/* Persons */}
              {recipeJob.input_data.selected_persons?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">👤 Personen</h4>
                  <div className="flex flex-wrap gap-2">
                    {recipeJob.input_data.selected_persons.map((person, idx) => (
                      <Badge key={idx} variant="secondary">
                        <Users className="h-3 w-3 mr-1" />
                        {getPersonName(person)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Logos */}
              {recipeJob.input_data.selected_logos?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">🏢 Logos</h4>
                  <Badge variant="secondary">
                    <Building2 className="h-3 w-3 mr-1" />
                    {recipeJob.input_data.selected_logos.length} Logo(s) verwendet
                  </Badge>
                </div>
              )}

              {/* Logo Position */}
              {recipeJob.input_data.logo_position && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">📍 Logo-Position</h4>
                  <Badge variant="outline">{recipeJob.input_data.logo_position}</Badge>
                </div>
              )}

              {/* Variations */}
              {recipeJob.input_data.variations && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">🔢 Varianten</h4>
                  <Badge variant="outline">{recipeJob.input_data.variations} Variante(n) generiert</Badge>
                </div>
              )}

              {/* Questions/Answers if available */}
              {recipeJob.input_data.questions && Object.keys(recipeJob.input_data.questions).length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">❓ Fragen & Antworten</h4>
                  <div className="space-y-2">
                    {Object.entries(recipeJob.input_data.questions).map(([q, a], idx) => (
                      <div key={idx} className="bg-muted p-2 rounded-lg text-sm">
                        <p className="text-muted-foreground">{q}</p>
                        <p className="font-medium">{a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Prompt if available */}
              {recipeJob.input_data.custom_prompt && (
                <div className="space-y-1">
                  <h4 className="text-sm font-medium text-muted-foreground">💬 Custom Prompt</h4>
                  <p className="text-xs bg-muted p-3 rounded-lg font-mono whitespace-pre-wrap">
                    {recipeJob.input_data.custom_prompt}
                  </p>
                </div>
              )}
            </div>
          )}

          <DrawerFooter className="border-t">
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setRecipeJob(null)}>
                Schließen
              </Button>
              <Button 
                variant="outline"
                className="flex-1"
                onClick={() => {
                  // Copy recipe to clipboard
                  const recipe = JSON.stringify(recipeJob?.input_data, null, 2);
                  navigator.clipboard.writeText(recipe);
                  toast.success("Einstellungen kopiert!");
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Kopieren
              </Button>
              <Button 
                className="flex-1 bg-primary"
                onClick={async () => {
                  // Recreate thumbnail with same settings
                  try {
                    const inputData = recipeJob?.input_data;
                    if (!inputData) {
                      toast.error("Keine Einstellungen verfügbar");
                      return;
                    }
                    
                    toast.info("Erstelle neues Thumbnail mit gleichen Einstellungen...");
                    setRecipeJob(null);
                    
                    const response = await axios.post(`${API}/jobs/generator2`, {
                      video_title: inputData.video_title,
                      video_description: inputData.video_description || "",
                      style_id: inputData.style_id,
                      brand_colors: inputData.brand_colors || [],
                      tone_case: inputData.tone_case || "normal",
                      selected_persons: inputData.selected_persons || [],
                      variations: inputData.variations || 1
                    });
                    
                    if (response.data?.id) {
                      toast.success("Neuer Auftrag gestartet!");
                      fetchJobs(false);
                    }
                  } catch (err) {
                    console.error("Recreate error:", err);
                    toast.error("Fehler beim Erstellen: " + (err.response?.data?.detail || err.message));
                  }
                }}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Neu erstellen
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        itemId={shareTarget?.id}
        itemType="jobs"
        itemName={shareTarget?.name || shareTarget?.style_name || "Auftrag"}
        sharedWith={shareTarget?.shared_with || []}
        onShareUpdate={() => fetchJobs(false)}
      />

      {/* Story Preview Dialog (9:16 Meta Ads) */}
      <StoryPreviewDialog
        open={!!storyPreviewSrc}
        onOpenChange={(o) => { if (!o) setStoryPreviewSrc(null); }}
        imageSrc={storyPreviewSrc}
        accountName={selectedJob?.title?.slice(0, 24) || "deinkonto"}
        ctaText={selectedJob?.input_data?.cta || "Mehr erfahren"}
      />

      {/* Carousel Preview Dialog */}
      <StoryPreviewDialog
        open={!!carouselPreviewSlides}
        onOpenChange={(o) => { if (!o) setCarouselPreviewSlides(null); }}
        carouselSlides={carouselPreviewSlides || []}
        accountName={selectedJob?.title?.slice(0, 24) || "deinkonto"}
        ctaText={selectedJob?.input_data?.cta || "Mehr erfahren"}
      />

      {/* Save as Vorlage Dialog */}
      <AlertDialog open={savePresetDialogOpen} onOpenChange={setSavePresetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Als Vorlage speichern</AlertDialogTitle>
            <AlertDialogDescription>
              Alle Einstellungen dieses Auftrags werden als wiederverwendbare Vorlage gespeichert.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Input value={savePresetName} onChange={e => setSavePresetName(e.target.value)} placeholder="z.B. Recruiting Pflegefachkraft" data-testid="save-job-preset-name" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveJobAsPreset} disabled={!savePresetName.trim()} data-testid="save-job-preset-confirm">
              <Save className="h-4 w-4 mr-2" />
              Speichern
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============== Persons Library Page ==============
const PersonsPage = () => {
  const [persons, setPersons] = useState([]);
  const [styles, setStyles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  
  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);

  // Edit name state
  const [editingPersonId, setEditingPersonId] = useState(null);
  const [editName, setEditName] = useState('');

  // Folder state + handlers (shared hook)
  const {
    folders,
    selectedFolderId,
    setSelectedFolderId,
    newFolderName,
    setNewFolderName,
    showNewFolder,
    setShowNewFolder,
    editingFolderId,
    setEditingFolderId,
    editFolderName,
    setEditFolderName,
    currentUser,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleToggleShareFolder,
    handleMoveToFolder,
    handleBulkMoveToFolder: bulkMoveToFolder,
    filterByFolder,
  } = useFolders({
    folderType: 'persons',
    itemEndpoint: '/persons',
    setItems: setPersons,
    itemNounSingular: 'Person',
    itemNounPlural: 'Personen',
  });

  // Bulk selection (shared hook)
  const { selectedIds, bulkMode, setBulkMode, setSelectedIds, toggleSelect, clearSelection } = useBulkSelection();

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Upload form state
  const [uploadName, setUploadName] = useState('');
  const [uploadRole, setUploadRole] = useState('');
  const [uploadImage, setUploadImage] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isImprovingRole, setIsImprovingRole] = useState(false);

  // Fetch persons
  const fetchPersons = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/persons`);
      setPersons(response.data);
    } catch (err) {
      console.error('Error fetching persons:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPersons();
  }, [fetchPersons]);

  // Fetch styles for name resolution
  useEffect(() => {
    const fetchStyles = async () => {
      try {
        const response = await axios.get(`${API}/styles`);
        setStyles(response.data);
      } catch (err) {
        console.error('Error fetching styles:', err);
      }
    };
    fetchStyles();
  }, []);

  // Helper function to get person name from ID
  const getPersonName = (personIdOrObj) => {
    if (typeof personIdOrObj === 'object' && personIdOrObj?.name) {
      return personIdOrObj.name;
    }
    const person = persons.find(p => p.id === personIdOrObj);
    return person?.name || 'Unbekannte Person';
  };

  // Helper function to get style name from ID
  const getStyleName = (styleIdOrName) => {
    if (!styleIdOrName) return null;
    // If it's already a name (not UUID format), return it
    if (styleIdOrName && !styleIdOrName.match(/^[0-9a-f]{8}-[0-9a-f]{4}-/)) {
      return styleIdOrName;
    }
    const style = styles.find(s => s.id === styleIdOrName);
    return style?.name || styleIdOrName;
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!validateImageFile(file)) return;

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadPreview(reader.result);
      // Extract base64 data (remove data:image/xxx;base64, prefix)
      const base64 = reader.result.split(',')[1];
      setUploadImage({ data: base64, mime: file.type });
    };
    reader.readAsDataURL(file);
  };

  // Improve role with AI (magic wand)
  const handleImproveRole = async () => {
    if (!uploadRole.trim()) {
      toast.error('Bitte zuerst eine Rolle eingeben');
      return;
    }

    setIsImprovingRole(true);
    try {
      const response = await axios.post(`${API}/persons/improve-role`, {
        role_text: uploadRole
      });
      setUploadRole(response.data.improved);
      toast.success('Rolle verbessert!');
    } catch (err) {
      toast.error('Fehler bei der Verbesserung');
    } finally {
      setIsImprovingRole(false);
    }
  };

  // Upload person
  const handleUpload = async () => {
    if (!uploadName.trim()) {
      toast.error('Bitte einen Namen eingeben');
      return;
    }
    if (!uploadImage) {
      toast.error('Bitte ein Bild auswählen');
      return;
    }

    setIsUploading(true);
    try {
      await axios.post(`${API}/persons`, {
        name: uploadName,
        role: uploadRole,
        image_data: uploadImage.data,
        image_mime_type: uploadImage.mime
      });

      toast.success(`Person "${uploadName}" hinzugefügt!`);
      
      // Reset form
      setUploadName('');
      setUploadRole('');
      setUploadImage(null);
      setUploadPreview(null);
      setShowUploadDialog(false);
      
      // Refresh list
      fetchPersons();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setIsUploading(false);
    }
  };

  // Delete person
  const handleDeletePerson = async (personId) => {
    try {
      await axios.delete(`${API}/persons/${personId}`);
      setPersons(prev => prev.filter(p => p.id !== personId));
      if (selectedPerson?.id === personId) setSelectedPerson(null);
      toast.success('Person gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Rename person
  const handleRenamePerson = async (personId) => {
    const trimmed = editName.trim();
    if (!trimmed) {
      setEditingPersonId(null);
      return;
    }
    try {
      await axios.put(`${API}/persons/${personId}`, { name: trimmed });
      setPersons(prev => prev.map(p => p.id === personId ? { ...p, name: trimmed } : p));
      toast.success('Name aktualisiert');
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Nur der Ersteller kann den Namen ändern');
      } else {
        toast.error('Fehler beim Umbenennen');
      }
    } finally {
      setEditingPersonId(null);
    }
  };

  // View person details
  const handleViewPerson = async (person) => {
    try {
      const response = await axios.get(`${API}/persons/${person.id}`);
      setSelectedPerson(response.data);
    } catch (err) {
      toast.error('Fehler beim Laden');
    }
  };

  // Bulk move selected persons (wraps shared hook with selection cleanup)
  const handleBulkMoveToFolder = async (folderId) => {
    await bulkMoveToFolder(folderId, selectedIds, clearSelection);
  };

  // Filtered persons: folder + search
  const filteredPersons = (() => {
    let result = filterByFolder(persons);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.name?.toLowerCase().includes(q) || p.role?.toLowerCase().includes(q));
    }
    return result;
  })();

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="persons-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            Personen-Bibliothek
          </h1>
          <p className="text-muted-foreground mt-1">
            Lade Personen mit Rollen hoch für personalisierte Thumbnails
          </p>
        </div>
        <Button onClick={() => setShowUploadDialog(true)} data-testid="add-person-btn">
          <Plus className="h-4 w-4 mr-2" />
          Neue Person
        </Button>
      </div>

      {/* Upload Dialog */}
      <Sheet open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Neue Person hinzufügen</SheetTitle>
            <SheetDescription>
              Lade ein Bild hoch und beschreibe die Rolle der Person
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-6 mt-6">
            {/* Image Upload */}
            <div>
              <label className="text-sm font-medium mb-2 block">Bild</label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                {uploadPreview ? (
                  <div className="space-y-2">
                    <img
                      src={uploadPreview}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg mx-auto"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setUploadImage(null); setUploadPreview(null); }}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Entfernen
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer block py-4">
                    <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Klicke zum Hochladen</p>
                    <p className="text-xs text-muted-foreground">Max. 5MB (JPG, PNG)</p>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileSelect}
                      data-testid="person-image-input"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="text-sm font-medium mb-2 block">Name</label>
              <Input
                placeholder="z.B. Max Mustermann"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                data-testid="person-name-input"
              />
            </div>

            {/* Role with Magic Wand */}
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <Briefcase className="h-4 w-4" />
                  Rolle
                </span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleImproveRole}
                        disabled={isImprovingRole || !uploadRole.trim()}
                        className="h-7 px-2"
                        data-testid="improve-role-btn"
                      >
                        {isImprovingRole ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Wand2 className="h-4 w-4 text-accent" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Mit KI verbessern</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </label>
              <Textarea
                placeholder="z.B. Kunde aus Testimonial, zufrieden mit dem Produkt"
                value={uploadRole}
                onChange={(e) => setUploadRole(e.target.value)}
                rows={3}
                data-testid="person-role-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Beschreibe die Rolle kurz. Klicke auf 🪄 für KI-Verbesserung.
              </p>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleUpload}
              disabled={isUploading || !uploadName.trim() || !uploadImage}
              className="w-full"
              data-testid="upload-person-btn"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird hochgeladen...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Person hinzufügen
                </>
              )}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Search + Bulk Actions Bar */}
      {!isLoading && persons.length > 0 && (
        <div className="flex items-center gap-3 mb-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              placeholder="Person suchen..."
              className="w-full h-9 pl-9 pr-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            variant={bulkMode ? "default" : "outline"}
            size="sm"
            onClick={() => { setBulkMode(!bulkMode); setSelectedIds(new Set()); }}
          >
            {bulkMode ? `${selectedIds.size} ausgewählt` : 'Auswählen'}
          </Button>
          {bulkMode && selectedIds.size > 0 && folders.length > 0 && (
            <select
              className="h-9 text-sm border rounded-lg px-2 bg-background cursor-pointer"
              value=""
              onChange={(e) => { if (e.target.value) handleBulkMoveToFolder(e.target.value); }}
            >
              <option value="">In Ordner verschieben...</option>
              <option value="">── Kein Ordner ──</option>
              {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          )}
        </div>
      )}

      {/* Folder Tabs */}
      {!isLoading && persons.length > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <button
            onClick={() => setSelectedFolderId('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              selectedFolderId === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80 text-muted-foreground'
            }`}
          >
            Alle ({persons.length})
          </button>
          <button
            onClick={() => setSelectedFolderId('none')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              selectedFolderId === 'none' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80 text-muted-foreground'
            }`}
          >
            Ohne Ordner ({persons.filter(p => !p.folder_id).length})
          </button>
          {folders.map(folder => (
            <div key={folder.id} className="group relative flex items-center">
              {editingFolderId === folder.id ? (
                <input
                  autoFocus
                  className="px-3 py-1.5 rounded-full text-sm border focus:outline-none focus:ring-1 focus:ring-primary w-32"
                  value={editFolderName}
                  onChange={(e) => setEditFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRenameFolder(folder.id);
                    if (e.key === 'Escape') setEditingFolderId(null);
                  }}
                  onBlur={() => handleRenameFolder(folder.id)}
                />
              ) : (
                <button
                  onClick={() => setSelectedFolderId(folder.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    selectedFolderId === folder.id ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                  }`}
                >
                  <FolderOpen className="h-3.5 w-3.5" />
                  {folder.name} ({persons.filter(p => p.folder_id === folder.id).length})
                  {folder.shared_with_org && <Share2 className="h-3 w-3 opacity-60" />}
                </button>
              )}
              {/* Folder actions on hover */}
              {(folder.owner_id === currentUser?.id || currentUser?.role === 'owner') && editingFolderId !== folder.id && (
                <div className="hidden group-hover:flex items-center gap-0.5 ml-1">
                  <button onClick={(e) => { e.stopPropagation(); setEditingFolderId(folder.id); setEditFolderName(folder.name); }} className="p-1 rounded hover:bg-muted" title="Umbenennen">
                    <Pencil className="h-3 w-3 text-muted-foreground" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleToggleShareFolder(folder); }} className="p-1 rounded hover:bg-muted" title={folder.shared_with_org ? 'Freigabe aufheben' : 'Mit Organisation teilen'}>
                    <Share2 className={`h-3 w-3 ${folder.shared_with_org ? 'text-primary' : 'text-muted-foreground'}`} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Ordner "${folder.name}" löschen?`)) handleDeleteFolder(folder.id); }} className="p-1 rounded hover:bg-muted" title="Löschen">
                    <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
              )}
            </div>
          ))}
          {/* New folder button */}
          {showNewFolder ? (
            <input
              autoFocus
              placeholder="Ordnername..."
              className="px-3 py-1.5 rounded-full text-sm border focus:outline-none focus:ring-1 focus:ring-primary w-32"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFolder();
                if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName(''); }
              }}
              onBlur={() => { if (newFolderName.trim()) handleCreateFolder(); else { setShowNewFolder(false); setNewFolderName(''); } }}
            />
          ) : (
            <button
              onClick={() => setShowNewFolder(true)}
              className="px-3 py-1.5 rounded-full text-sm font-medium border border-dashed border-muted-foreground/40 hover:border-primary hover:text-primary transition-colors flex items-center gap-1 text-muted-foreground"
            >
              <Plus className="h-3.5 w-3.5" />
              Ordner
            </button>
          )}
        </div>
      )}

      {/* Move to folder dropdown for selected folder view */}
      {selectedFolderId !== 'all' && filteredPersons.length > 0 && folders.length > 0 && (
        <p className="text-xs text-muted-foreground mb-3">
          Tipp: Ziehe Personen per Rechtsklick in einen anderen Ordner
        </p>
      )}

      {/* Persons Grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="aspect-square rounded-lg mb-3" />
                <Skeleton className="h-4 w-2/3 mb-2" />
                <Skeleton className="h-3 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredPersons.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredPersons.map(person => (
            <Card
              key={person.id}
              className={`cursor-pointer transition-all hover:shadow-md overflow-hidden ${bulkMode && selectedIds.has(person.id) ? 'ring-2 ring-primary' : ''}`}
              onClick={() => bulkMode ? toggleSelect(person.id) : handleViewPerson(person)}
              data-testid={`person-card-${person.id}`}
            >
              <CardContent className="p-0">
                {/* Image */}
                <div className="aspect-square overflow-hidden bg-muted relative">
                  <img
                    src={`${API}/persons/${person.id}/image?token=${localStorage.getItem('auth_token')}`}
                    alt={person.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  {bulkMode && (
                    <div className={`absolute top-2 left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${selectedIds.has(person.id) ? 'bg-primary border-primary text-white' : 'bg-background/80 border-muted-foreground/50'}`}>
                      {selectedIds.has(person.id) && <CheckCircle2 className="h-4 w-4" />}
                    </div>
                  )}
                </div>

                <div className="p-3">
                  {/* Info - Name with inline edit */}
                  {editingPersonId === person.id ? (
                    <input
                      autoFocus
                      className="font-medium w-full border rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenamePerson(person.id);
                        if (e.key === 'Escape') setEditingPersonId(null);
                      }}
                      onBlur={() => handleRenamePerson(person.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <div className="flex items-center gap-1 group">
                      <h3 className="font-medium text-sm truncate">{person.name}</h3>
                      {(person.owner_id === currentUser?.id || person.user_id === currentUser?.id) && (
                        <button
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingPersonId(person.id);
                            setEditName(person.name);
                          }}
                          title="Name ändern"
                        >
                          <Pencil className="h-3 w-3 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground truncate">
                    {person.role || 'Keine Rolle definiert'}
                  </p>

                  {/* Actions */}
                  <div className="flex items-center justify-between mt-2 gap-1">
                    {/* Folder select */}
                    {folders.length > 0 && (
                      <select
                        className="h-7 text-xs border rounded px-1 bg-background text-muted-foreground cursor-pointer min-w-0 flex-1 max-w-[120px]"
                        value={person.folder_id || ''}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => { e.stopPropagation(); handleMoveToFolder(person.id, e.target.value); }}
                      >
                        <option value="">Kein Ordner</option>
                        {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                    )}
                    {/* Share Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-primary flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShareTarget(person);
                        setShareDialogOpen(true);
                      }}
                      data-testid={`share-person-${person.id}`}
                    >
                      <Share2 className="h-3.5 w-3.5" />
                    </Button>
                    {/* Delete Button */}
                    <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Person löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                          "{person.name}" wird unwiderruflich gelöscht.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeletePerson(person.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Löschen
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
              <h3 className="font-medium mb-1">Noch keine Personen</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Füge Personen hinzu, um sie in Thumbnails zu verwenden
              </p>
              <Button onClick={() => setShowUploadDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Erste Person hinzufügen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Person Detail Sheet */}
      <Sheet open={!!selectedPerson} onOpenChange={() => setSelectedPerson(null)}>
        <SheetContent className="w-full sm:max-w-md">
          {selectedPerson && (
            <>
              <SheetHeader>
                <SheetTitle>{selectedPerson.name}</SheetTitle>
                <SheetDescription>Person in der Bibliothek</SheetDescription>
              </SheetHeader>

              <div className="space-y-6 mt-6">
                {/* Image */}
                <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                  {selectedPerson.image_data && (
                    <img
                      src={`data:${selectedPerson.image_mime_type || 'image/jpeg'};base64,${selectedPerson.image_data}`}
                      alt={selectedPerson.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Role */}
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-primary" />
                    Rolle
                  </h4>
                  <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
                    {selectedPerson.role || 'Keine Rolle definiert'}
                  </p>
                </div>

                {/* Usage Stats */}
                <div className="text-xs text-muted-foreground border-t pt-4">
                  Verwendet in {selectedPerson.usage_count || 0} Thumbnails • 
                  Erstellt am {new Date(selectedPerson.created_at).toLocaleDateString('de-DE')}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        itemId={shareTarget?.id}
        itemType="persons"
        itemName={shareTarget?.name || "Person"}
        sharedWith={shareTarget?.shared_with || []}
        onShareUpdate={fetchPersons}
      />
    </div>
  );
};

// ============== App Layout ==============
const AppLayout = ({ children, user, onLogout, hasUnreadUpdates, onOpenWhatsNew }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeJobsCount, setActiveJobsCount] = useState(0);
  const [commandOpen, setCommandOpen] = useState(false);
  const [styles, setStyles] = useState([]);

  // Keyboard shortcut for Command Menu
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Fetch styles for command menu
  useEffect(() => {
    const fetchStyles = async () => {
      try {
        const response = await axios.get(`${API}/styles`);
        setStyles(response.data.filter(s => s.status === 'completed'));
      } catch (err) {
        // Silently fail
      }
    };
    fetchStyles();
  }, []);

  // Poll for active jobs count (every 15s to reduce load)
  useEffect(() => {
    const fetchActiveJobs = async () => {
      try {
        const response = await axios.get(`${API}/jobs/active`);
        setActiveJobsCount(response.data.length);
      } catch (err) {
        // Silently fail
      }
    };
    
    fetchActiveJobs();
    const interval = setInterval(fetchActiveJobs, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleCommandSelect = (value) => {
    setCommandOpen(false);
    
    // Navigation commands
    if (value.startsWith('nav:')) {
      navigate(value.replace('nav:', ''));
    }
    // Style selection
    else if (value.startsWith('style:')) {
      navigate(`/styles/${value.replace('style:', '')}`);
    }
    // Quick actions
    else if (value === 'action:new-analysis') {
      navigate('/');
    }
    else if (value === 'action:new-thumbnail') {
      navigate('/generator');
    }
  };

  return (
    <div className="app-shell">
      {/* Command Menu */}
      <CommandDialog open={commandOpen} onOpenChange={setCommandOpen}>
        <Command className="rounded-lg border shadow-md">
          <CommandInput placeholder="Suche nach Seiten, Styles, Aktionen..." data-testid="command-menu-input" />
          <CommandList>
            <CommandEmpty>Keine Ergebnisse gefunden.</CommandEmpty>
            
            <CommandGroup heading="Schnellaktionen">
              <CommandItem 
                onSelect={() => handleCommandSelect('action:new-analysis')}
                data-testid="cmd-new-analysis"
              >
                <Zap className="mr-2 h-4 w-4 text-primary" />
                <span>Neue Video-Analyse</span>
              </CommandItem>
              <CommandItem 
                onSelect={() => handleCommandSelect('action:new-thumbnail')}
                data-testid="cmd-new-thumbnail"
              >
                <Sparkles className="mr-2 h-4 w-4 text-accent" />
                <span>Neues Thumbnail generieren</span>
              </CommandItem>
            </CommandGroup>
            
            <CommandSeparator />
            
            <CommandGroup heading="Navigation">
              <CommandItem onSelect={() => handleCommandSelect('nav:/')}>
                <Zap className="mr-2 h-4 w-4" />
                <span>Video Analyse</span>
              </CommandItem>
              <CommandItem onSelect={() => handleCommandSelect('nav:/styles')}>
                <Palette className="mr-2 h-4 w-4" />
                <span>Style Library</span>
              </CommandItem>
              <CommandItem onSelect={() => handleCommandSelect('nav:/generator')}>
                <Sparkles className="mr-2 h-4 w-4" />
                <span>Thumbnail Generator</span>
              </CommandItem>
              <CommandItem onSelect={() => handleCommandSelect('nav:/persons')}>
                <Users className="mr-2 h-4 w-4" />
                <span>Personen-Bibliothek</span>
              </CommandItem>
              <CommandItem onSelect={() => handleCommandSelect('nav:/logos')}>
                <Building2 className="mr-2 h-4 w-4" />
                <span>Logo-Bibliothek</span>
              </CommandItem>
              <CommandItem onSelect={() => handleCommandSelect('nav:/jobs')}>
                <Activity className="mr-2 h-4 w-4" />
                <span>Meine Aufträge</span>
                {activeJobsCount > 0 && (
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {activeJobsCount} aktiv
                  </Badge>
                )}
              </CommandItem>
              <CommandItem onSelect={() => handleCommandSelect('nav:/history')}>
                <Clock className="mr-2 h-4 w-4" />
                <span>Verlauf</span>
              </CommandItem>
            </CommandGroup>
            
            {styles.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Styles">
                  {styles.slice(0, 5).map((style) => (
                    <CommandItem 
                      key={style.id}
                      onSelect={() => handleCommandSelect(`style:${style.id}`)}
                    >
                      <div className="flex items-center gap-2 flex-1">
                        {style.aggregated_style?.dominant_palette?.[0]?.hex && (
                          <div 
                            className="w-3 h-3 rounded-full border"
                            style={{ backgroundColor: style.aggregated_style.dominant_palette[0].hex }}
                          />
                        )}
                        <span>{style.name}</span>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {style.channel_name}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                  {styles.length > 5 && (
                    <CommandItem onSelect={() => handleCommandSelect('nav:/styles')}>
                      <span className="text-muted-foreground">
                        + {styles.length - 5} weitere Styles anzeigen
                      </span>
                    </CommandItem>
                  )}
                </CommandGroup>
              </>
            )}

            <CommandSeparator />
            
            <CommandGroup heading="Psychologie-Glossar">
              <CommandItem onSelect={() => toast.info("Curiosity Gap: Erzeugt ein Informationsdefizit, das der Viewer schließen will.")}>
                <Brain className="mr-2 h-4 w-4 text-purple-500" />
                <span>Curiosity Gap</span>
                <span className="text-xs text-muted-foreground ml-auto">Kognitiver Bias</span>
              </CommandItem>
              <CommandItem onSelect={() => toast.info("FOMO: Fear of Missing Out - Angst etwas Wichtiges zu verpassen.")}>
                <Brain className="mr-2 h-4 w-4 text-purple-500" />
                <span>FOMO</span>
                <span className="text-xs text-muted-foreground ml-auto">Kognitiver Bias</span>
              </CommandItem>
              <CommandItem onSelect={() => toast.info("Social Proof: Menschen orientieren sich am Verhalten anderer.")}>
                <Brain className="mr-2 h-4 w-4 text-purple-500" />
                <span>Social Proof</span>
                <span className="text-xs text-muted-foreground ml-auto">Kognitiver Bias</span>
              </CommandItem>
              <CommandItem onSelect={() => toast.info("Große Zahlen: Zahlen wie $1,000,000 aktivieren das Belohnungszentrum.")}>
                <Zap className="mr-2 h-4 w-4 text-orange-500" />
                <span>Große Zahlen</span>
                <span className="text-xs text-muted-foreground ml-auto">Dopamin-Trigger</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </CommandDialog>

      {/* Desktop Sidebar */}
      <aside className="sidebar hidden lg:block">
        <Sidebar currentPath={location.pathname} activeJobsCount={activeJobsCount} />
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <Topbar currentPath={location.pathname} onOpenCommandMenu={() => setCommandOpen(true)} user={user} onLogout={onLogout} hasUnreadUpdates={hasUnreadUpdates} onOpenWhatsNew={onOpenWhatsNew} />
        <main className="content-area">
          {children}
        </main>
      </div>
    </div>
  );
};

// ============== Main App ==============
function App() {
  const [user, setUser] = useState(getUser());
  const [isAuthenticated, setIsAuthenticated] = useState(!!getAuthToken());
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [hasUnreadUpdates, setHasUnreadUpdates] = useState(false);

  // Check if user needs to see "What's New" modal
  useEffect(() => {
    if (!isAuthenticated) return;
    const checkChangelog = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/seen-changelog`);
        const seenVersion = res.data?.version;
        if (seenVersion !== WHATS_NEW_VERSION) {
          setShowWhatsNew(true);
          setHasUnreadUpdates(true);
        }
      } catch { /* ignore */ }
    };
    // Small delay so the app renders first
    const timer = setTimeout(checkChangelog, 800);
    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  const handleLogin = (userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  // Check for account setup route (accessible without auth)
  const urlParams = new URLSearchParams(window.location.search);
  const setupToken = urlParams.get('token');
  const isSetupRoute = window.location.pathname === '/konto-einrichten';
  const isPasswordResetRoute = window.location.pathname === '/passwort-reset';

  // Show account setup page if on setup route
  if (isSetupRoute && setupToken) {
    return (
      <TooltipProvider>
        <Toaster position="top-right" richColors />
        <AccountSetupPage token={setupToken} />
      </TooltipProvider>
    );
  }

  // Show password reset page if on reset route
  if (isPasswordResetRoute) {
    const resetToken = urlParams.get('token');
    return (
      <TooltipProvider>
        <Toaster position="top-right" richColors />
        <PasswordResetPage token={resetToken} />
      </TooltipProvider>
    );
  }

  // If not authenticated, show login page
  if (!isAuthenticated) {
    return (
      <TooltipProvider>
        <Toaster position="top-right" richColors />
        <LoginPage onLogin={handleLogin} />
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        {showWhatsNew && (
          <WhatsNewModal 
            onDismiss={() => setShowWhatsNew(false)} 
            onMarkRead={() => setHasUnreadUpdates(false)}
          />
        )}
        <AppLayout user={user} onLogout={handleLogout} hasUnreadUpdates={hasUnreadUpdates} onOpenWhatsNew={() => setShowWhatsNew(true)}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/styles" element={<StyleLibraryPage />} />
            <Route path="/styles/:styleId" element={<StyleDetailPage />} />
            <Route path="/persons" element={<PersonsPage />} />
            <Route path="/logos" element={<LogoLibraryPage />} />
            <Route path="/brand-kits" element={<BrandKitsPage />} />
            <Route path="/generator" element={<Generator2Page />} />
            <Route path="/script" element={<ScriptPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/editor" element={<ThumbnailEditor />} />
            <Route path="/meta-ad-editor" element={<MetaAdEditor />} />
            <Route path="/carousel" element={<CarouselGenerator />} />
            <Route path="/meta-generator" element={<MetaAdsGenerator />} />
            <Route path="/meta-templates" element={<MetaAdTemplatesLibrary />} />
            <Route path="/comparison-ads" element={<ComparisonAdGenerator />} />
            {/* LinkedIn Carousel removed */}
            <Route path="/profile" element={<ProfilePage user={user} onUserUpdate={(u) => { setUser(u); localStorage.setItem("user", JSON.stringify(u)); }} />} />
            {(user?.role === "admin" || user?.role === "owner") && (
              <Route path="/admin" element={<AdminPanel currentUser={user} />} />
            )}
          </Routes>
        </AppLayout>
      </BrowserRouter>
    </TooltipProvider>
  );
}

export default App;
